import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useMedia, serializeDate, hydrateDate } from '../../contexts/MediaContext';
import { useMediaWebSocket } from '../../hooks/useMediaWebSocket';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';
import {
  Download, Search, Loader2, HardDrive, RefreshCw, Play,
  Camera, X, CheckCircle, XCircle, AlertCircle, Clock,
  Calendar as CalendarIcon, Square, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, List, Activity,
  Clapperboard, Volume2, VolumeX, Maximize2, Minimize2, Trash2,
} from 'lucide-react';
import { VideoPlayerModal } from './VideoPlayerModal';
import NativeWebCodecsPlayer from './NativeWebCodecsPlayer';
import { resolveTimezone, formatDT } from '../../utils/timezone';
import { getCameraRecordingState } from '../../utils/cameraRecordingState';
import { deviceDisplayName } from '../../utils/deviceName';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Calendar as CalendarPicker } from '../ui/calendar';
import { format as _dfFormat } from 'date-fns';
import { PaginationFooter, paginateArray, usePersistedPageSize } from '../shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useSkipFirstEffect } from '../shared/useUrlPagination';
import { DateRangePresetFilter } from '../shared/DateRangePresetFilter';

/**
 * Human-readable byte size — 144.22 MB / 2.30 GB / 512 KB.
 */
const humanFileSize = (bytes) => {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 10 ? 1 : 2)} ${units[i]}`;
};

const isMeitrackVideoDevice = (device) => {
  if (!device) return false;
  return [device.protocol, device.device_type, device.model, device.manufacturer]
    .some(value => String(value || '').toLowerCase().includes('meitrack'))
    || String(device.model || '').toLowerCase().includes('md300');
};

const STATUS_BADGE = {
  pending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  uploading: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  finalizing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  verifying: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  missing: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  cancelled: 'bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-400',
};

const pad = n => String(n).padStart(2, '0');

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const dateKey = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const parseTimeParts = (value, fallback) => {
  const [h = fallback[0], m = fallback[1], s = fallback[2]] = String(value || '').split(':').map(v => parseInt(v, 10));
  return [Number.isFinite(h) ? h : fallback[0], Number.isFinite(m) ? m : fallback[1], Number.isFinite(s) ? s : fallback[2]];
};

const localDayTimeToDeviceIso = (date, timeValue, fallback) => {
  const [hour, minute, second] = parseTimeParts(timeValue, fallback);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
};

const localIsoToSeconds = (iso, { endOfDay = false } = {}) => {
  if (!iso) return 0;
  const time = iso.split('T')[1] || '00:00:00';
  const [h, m, s] = parseTimeParts(time, [0, 0, 0]);
  const total = h * 3600 + m * 60 + s;
  if (endOfDay && h === 0 && m === 0 && s <= 2) return 86400;
  return Math.max(0, Math.min(endOfDay ? 86400 : 86399, total));
};

const secondsLabel = (seconds) => `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}`;

const clockSecondsLabel = (seconds) => `${secondsLabel(Math.max(0, seconds))}:${pad(Math.max(0, seconds) % 60)}`;

const VIDEO_MODULE_OFF_TOAST_ID = 'remote-media-video-module-off';

const clampNumber = (value, min, max) => Math.max(min, Math.min(max, value));

const parseWallIsoParts = (iso) => {
  if (!iso) return null;
  const [datePart, timePart = '00:00:00'] = String(iso).split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = parseTimeParts(timePart, [0, 0, 0]);
  if (!year || !month || !day) return null;
  return { year, month, day, hour, minute, second };
};

const formatWallClock = (iso, { includeDate = true, seconds = true } = {}) => {
  const p = parseWallIsoParts(iso);
  if (!p) return '-';
  const hour12 = p.hour % 12 || 12;
  const ampm = p.hour >= 12 ? 'PM' : 'AM';
  const time = `${hour12}:${pad(p.minute)}${seconds ? `:${pad(p.second)}` : ''} ${ampm}`;
  return includeDate ? `${p.day} ${MONTHS_SHORT[p.month - 1]} ${p.year}, ${time}` : time;
};

const wallIsoToMs = (iso) => {
  const p = parseWallIsoParts(iso);
  if (!p) return null;
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second || 0);
};

const wallIsoFromMs = (ms) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
};

const bcdToWallMs = (bcd) => {
  const value = String(bcd || '').replace(/[^0-9]/g, '');
  if (value.length < 12) return null;
  const year = 2000 + parseInt(value.slice(0, 2), 10);
  const month = parseInt(value.slice(2, 4), 10);
  const day = parseInt(value.slice(4, 6), 10);
  const hour = parseInt(value.slice(6, 8), 10);
  const minute = parseInt(value.slice(8, 10), 10);
  const second = parseInt(value.slice(10, 12), 10);
  if ([year, month, day, hour, minute, second].some(v => !Number.isFinite(v))) return null;
  return Date.UTC(year, month - 1, day, hour, minute, second);
};

const bcdFromWallMs = (ms) => {
  const d = new Date(ms);
  return `${pad(d.getUTCFullYear() % 100)}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}`;
};

const addSecondsToBcd = (bcd, seconds) => {
  const ms = bcdToWallMs(bcd);
  if (ms == null) return null;
  return bcdFromWallMs(ms + Math.round(seconds || 0) * 1000);
};

const durationLabel = (startIso, endIso) => {
  const startMs = wallIsoToMs(startIso);
  const endMs = wallIsoToMs(endIso);
  let diff = 0;
  if (startMs != null && endMs != null && endMs > startMs) {
    diff = Math.round((endMs - startMs) / 1000);
  } else {
    const start = localIsoToSeconds(startIso);
    const end = localIsoToSeconds(endIso, { endOfDay: true });
    diff = end > start ? end - start : 0;
  }
  const h = Math.floor(diff / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (h) return `${h}h ${m}m ${s}s`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
};

const customDownloadWindowSeconds = (startIso, endIso) => {
  const startMs = wallIsoToMs(startIso);
  const endMs = wallIsoToMs(endIso);
  if (startMs == null || endMs == null) return null;
  return Math.round((endMs - startMs) / 1000);
};

const wallIsoFromDateAndSeconds = (date, seconds) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${secondsLabel(seconds)}:${pad(seconds % 60)}`;

const buildMonthDays = (monthDate) => {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, idx) => {
    const d = new Date(start);
    d.setDate(start.getDate() + idx);
    return d;
  });
};

// iter148: tick ladder for the Timeline graph, driven by the current zoom
// window `[zoomStart, zoomEnd]` in seconds-of-day. Picks the largest "nice"
// tick interval that produces ~6-12 ticks across the window so that labels
// stay readable whether the user is viewing a full 24h day or a 30-second
// alarm clip.
const NICE_TICK_STEPS = [
  1, 2, 5, 10, 15, 30,                  // seconds
  60, 120, 300, 600, 900, 1800,         // 1m, 2m, 5m, 10m, 15m, 30m
  3600, 7200, 10800, 21600, 43200,      // 1h, 2h, 3h, 6h, 12h
];

const buildTimelineTicks = (zoomStart, zoomEnd) => {
  const span = Math.max(1, zoomEnd - zoomStart);
  const targetTicks = 10;
  const rawStep = span / targetTicks;
  const step = NICE_TICK_STEPS.find(s => s >= rawStep) || 86400;
  const first = Math.ceil(zoomStart / step) * step;
  const ticks = [];
  for (let t = first; t <= zoomEnd + 0.5; t += step) {
    const pct = ((t - zoomStart) / span) * 100;
    ticks.push({ seconds: t, pct, label: formatTickLabel(t, step) });
    if (ticks.length > 20) break;
  }
  return ticks;
};

const formatTickLabel = (seconds, step) => {
  const totalSec = Math.max(0, Math.min(86400, seconds));
  // iter148.1 — keep "24:00" at the end of the day rather than wrapping
  // to "00:00". The user explicitly asked for 24:00 to remain.
  const isEndOfDay = totalSec >= 86400;
  const h = isEndOfDay ? 24 : Math.floor(totalSec / 3600) % 24;
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (step < 60) return `${pad(h)}:${pad(m)}:${pad(s)}`;
  return `${pad(h)}:${pad(m)}`;
};

const shiftTime = (value, deltaSeconds) => {
  const [h, m, s] = parseTimeParts(value, [0, 0, 0]);
  const total = (h * 3600 + m * 60 + s + deltaSeconds + 86400) % 86400;
  return `${secondsLabel(total)}:${pad(total % 60)}`;
};

const TimeStepper = ({ label, value, onChange, testId }) => (
  <div className="space-y-1 min-w-0" data-testid={`${testId}-control`}>
    <Label>{label}</Label>
    <div className="flex overflow-hidden rounded-md border bg-background dark:bg-[#172031] min-w-0">
      <Input
        type="time"
        step="1"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="h-9 min-w-0 flex-1 rounded-none border-0 px-2 text-xs sm:text-sm focus-visible:ring-0 dark:bg-[#172031]"
        data-testid={`${testId}-input`}
      />
      <div className="flex flex-shrink-0 flex-col border-l">
        <Button type="button" variant="ghost" size="icon" className="h-[18px] w-6 rounded-none text-[10px]" onClick={() => onChange(shiftTime(value, 60))} data-testid={`${testId}-increment-button`}>▲</Button>
        <Button type="button" variant="ghost" size="icon" className="h-[18px] w-6 rounded-none text-[10px]" onClick={() => onChange(shiftTime(value, -60))} data-testid={`${testId}-decrement-button`}>▼</Button>
      </div>
    </div>
  </div>
);

const CameraRecordingStatusDot = ({ device, className = 'h-2 w-2', testId }) => {
  const state = getCameraRecordingState(device);
  return (
    <span
      className={`${className} shrink-0 rounded-full ${state.isGprsOnline ? 'bg-emerald-500' : 'bg-slate-400'} ${state.hasAnyRecording ? 'ring-2 ring-red-600 dark:ring-red-400 ring-offset-1 ring-offset-background' : ''}`}
      title={state.hasAnyRecording ? 'Camera recording' : (state.isGprsOnline ? 'GPRS online' : 'GPRS offline')}
      data-testid={testId}
    />
  );
};

const STATUS_ICON = {
  pending: Loader2,
  uploading: Download,
  finalizing: Loader2,
  verifying: Loader2,
  completed: CheckCircle,
  missing: AlertCircle,
  failed: XCircle,
  cancelled: XCircle,
};

/**
 * "Download Custom Date" dialog — iter143 redesign.
 *
 * Lets the operator pick a single channel + date + start time +
 * preset duration (5 / 10 / 15 min) or a manual end time, and fires a
 * CE1 (A9F custom-window) download against the backend.
 *
 *   • Channel dropdown uses device.channel_names (→ "Front", "Cabin" …)
 *     with a `CH{n}` fallback for unnamed cameras.
 *   • Date picker is a plain <input type="date"> — fine for desktop
 *     browsers and avoids pulling in a heavy calendar lib.
 *   • 5 / 10 / 15-minute chips auto-compute the end time so ops don't
 *     have to do mental math.
 *   • BCD preview under the form shows the resolved window the backend
 *     will dispatch so mistakes are visible before clicking Download.
 *   • Submit dispatches ``onSubmit({ channel, startIso, endIso })`` to
 *     the parent which wraps the POST.
 */
const CustomDownloadDialog = ({
  open, onOpenChange, loading, channelList = [], defaultChannel,
  initialDate, initialStart = '00:00:00', displayTz, mediaTz, device = null, onSubmit,
}) => {
  const toYMD = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [channel, setChannel] = React.useState(defaultChannel);
  const [dateStr, setDateStr] = React.useState(initialDate ? toYMD(initialDate) : toYMD(new Date()));
  const [startTime, setStartTime] = React.useState(initialStart);
  const [endTime, setEndTime] = React.useState(initialStart);
  const [durationPreset, setDurationPreset] = React.useState(5);
  const [outputFormat, setOutputFormat] = React.useState('mp4');

  React.useEffect(() => {
    if (open) {
      setChannel(defaultChannel);
      setDateStr(initialDate ? toYMD(initialDate) : toYMD(new Date()));
      setStartTime(initialStart);
      // Default to start + 5 min end on dialog open.
      const [h, m, s] = parseTimeParts(initialStart, [0, 0, 0]);
      const totalSec = (h * 3600 + m * 60 + s + 5 * 60) % 86400;
      setEndTime(`${pad(Math.floor(totalSec / 3600))}:${pad(Math.floor((totalSec % 3600) / 60))}:${pad(totalSec % 60)}`);
      setDurationPreset(5);
      setOutputFormat('mp4');
    }
  }, [open, defaultChannel, initialDate, initialStart]);

  const applyPreset = (minutes) => {
    setDurationPreset(minutes);
    const [h, m, s] = parseTimeParts(startTime, [0, 0, 0]);
    const end = (h * 3600 + m * 60 + s + minutes * 60) % 86400;
    setEndTime(`${pad(Math.floor(end / 3600))}:${pad(Math.floor((end % 3600) / 60))}:${pad(end % 60)}`);
  };

  // When the start time changes, auto-recompute the end using the
  // current preset so the chips stay in sync.  If the user has typed
  // a manual end, they can still override afterwards.
  const handleStartChange = (v) => {
    setStartTime(v);
    const [h, m, s] = parseTimeParts(v, [0, 0, 0]);
    const end = (h * 3600 + m * 60 + s + durationPreset * 60) % 86400;
    setEndTime(`${pad(Math.floor(end / 3600))}:${pad(Math.floor((end % 3600) / 60))}:${pad(end % 60)}`);
  };

  const startIso = `${dateStr}T${startTime}`;
  const endIso = `${dateStr}T${endTime}`;
  const windowSeconds = customDownloadWindowSeconds(startIso, endIso);
  const exceedsMaxWindow = windowSeconds != null && windowSeconds > 15 * 60;
  const invalidWindow = windowSeconds == null || windowSeconds <= 0;
  const windowMinutes = windowSeconds != null && windowSeconds > 0 ? Math.floor(windowSeconds / 60) : 0;
  const windowRemainderSeconds = windowSeconds != null && windowSeconds > 0 ? windowSeconds % 60 : 0;

  const submit = () => {
    if (!channel) return;
    if (!dateStr || !startTime || !endTime) {
      toast.error('Pick a date, start time and end time');
      return;
    }
    if (startIso >= endIso) {
      toast.error('End time must be after start time');
      return;
    }
    if (exceedsMaxWindow) {
      toast.error('Custom downloads are limited to 15 minutes');
      return;
    }
    onSubmit({ channel, startIso, endIso, outputFormat });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="custom-download-dialog">
        <DialogHeader>
          <DialogTitle>Download Custom Date</DialogTitle>
          <DialogDescription>
            Pick the channel, date and time window to request a time-sliced download from the device.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Channel selector */}
          <div className="space-y-1.5">
            <Label>Channel</Label>
            <Select value={String(channel || '')} onValueChange={(v) => setChannel(Number(v))}>
              <SelectTrigger data-testid="custom-download-channel-select"><SelectValue placeholder="Select channel" /></SelectTrigger>
              <SelectContent>
                {channelList.map(ch => (
                  <SelectItem key={ch.id} value={String(ch.id)} data-testid={`custom-download-channel-option-${ch.id}`}>
                    <span className="flex items-center gap-2">
                      <Camera className="h-3 w-3" /> {ch.label}
                      {ch.label !== `CH${ch.id}` && <span className="text-xs text-muted-foreground">(CH{ch.id})</span>}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date picker */}
          <div className="space-y-1.5">
            <Label>Date</Label>
            <Input
              type="date"
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              data-testid="custom-download-date-input"
            />
          </div>

          {/* Start / End times */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start time</Label>
              <Input
                type="time"
                step="1"
                value={startTime}
                onChange={(e) => handleStartChange(e.target.value)}
                data-testid="custom-download-start-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>End time</Label>
              <Input
                type="time"
                step="1"
                value={endTime}
                onChange={(e) => { setEndTime(e.target.value); setDurationPreset(0); }}
                data-testid="custom-download-end-input"
              />
            </div>
          </div>

          {/* Preset chips */}
          <div className="space-y-1.5">
            <Label>Preset duration</Label>
            <div className="flex flex-wrap gap-2">
              {[5, 10, 15].map(mins => (
                <Button
                  key={mins}
                  type="button"
                  size="sm"
                  variant={durationPreset === mins ? 'default' : 'outline'}
                  onClick={() => applyPreset(mins)}
                  data-testid={`custom-download-preset-${mins}min`}
                >
                  {mins} min
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Format</Label>
            <Select value={outputFormat} onValueChange={setOutputFormat}>
              <SelectTrigger data-testid="custom-download-format-select"><SelectValue placeholder="Select format" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="mp4" data-testid="custom-download-format-option-mp4">MP4</SelectItem>
                <SelectItem value="original" data-testid="custom-download-format-option-original">Original</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Sanity preview — uses the user's display-timezone format
              via formatDT so it matches everywhere else in the app. */}
          <div className="rounded-md border bg-slate-50 p-2 text-[11px] text-slate-600 dark:bg-slate-900/40 dark:text-slate-300 space-y-0.5">
            <div>
              Window: <span className="font-medium">{formatDT(startIso, { device, seconds: true })}</span> → <span className="font-medium">{formatDT(endIso, { device, seconds: true, includeDate: false })}</span>
            </div>
            <div>
              Display TZ: <span className="font-medium">{displayTz || resolveTimezone({ device }) || '—'}</span> &nbsp;·&nbsp; Media TZ: <span className="font-medium">{mediaTz || 'UTC'}</span>
            </div>
            <div className="text-[10px] text-slate-500 italic pt-1" data-testid="custom-download-format-note">
              Format: <span className="font-medium">{outputFormat === 'mp4' ? 'MP4' : 'Original'}</span>
            </div>
            <div className={`text-[10px] pt-1 ${exceedsMaxWindow || invalidWindow ? 'text-red-600 font-medium' : 'text-slate-500'}`} data-testid="custom-download-duration-limit-note">
              Window length: <span className="font-medium">{invalidWindow ? 'Invalid' : `${windowMinutes}m ${windowRemainderSeconds}s`}</span> · Maximum 15m 0s
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="custom-download-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={loading || !channel || exceedsMaxWindow || invalidWindow} data-testid="custom-download-submit-button">
            {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />} Download
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Native remote-playback modal for A9D/A9E sessions.
 *
 * Uses the MD300 native TCP playback stream via ``NativeWebCodecsPlayer``.
 * Closing the modal stops device playback so cellular bandwidth is not
 * wasted streaming to nobody.
 */
const RemotePlaybackModal = ({ open, onClose, session, segment, channelLabel, deviceName, onStop, onSeek, api }) => {
  const playerRef = useRef(null);
  // Ref for the ENTIRE playback container (video + header + controls)
  // so fullscreen includes the controls bar — not just the video box.
  const modalContainerRef = useRef(null);
  const [status, setStatus] = useState('connecting');
  const [muted, setMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [seekValueSec, setSeekValueSec] = useState(0);
  const [isDraggingSeek, setIsDraggingSeek] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [transitionSnapshotUrl, setTransitionSnapshotUrl] = useState(null);
  const playbackClockRef = useRef({ sessionId: null, startedAt: Date.now(), startOffsetSec: 0 });

  const clearTransitionSnapshot = useCallback(() => {
    setTransitionSnapshotUrl((prev) => {
      if (prev?.startsWith?.('blob:') && typeof URL !== 'undefined') URL.revokeObjectURL(prev);
      return null;
    });
  }, []);

  const rangeStartIso = segment?.start_time || session?.range_start_utc || session?.start_time;
  const rangeEndIso = segment?.end_time || session?.range_end_utc || session?.end_time;
  const playbackStartIso = session?.range_start_utc || session?.start_time || rangeStartIso;
  const rangeStartMs = useMemo(() => wallIsoToMs(rangeStartIso), [rangeStartIso]);
  const rangeEndMs = useMemo(() => wallIsoToMs(rangeEndIso), [rangeEndIso]);
  const playbackStartMs = useMemo(() => wallIsoToMs(playbackStartIso), [playbackStartIso]);
  const durationSec = useMemo(() => {
    if (rangeStartMs == null || rangeEndMs == null) return 0;
    return Math.max(0, Math.round((rangeEndMs - rangeStartMs) / 1000));
  }, [rangeStartMs, rangeEndMs]);
  const currentSeekIso = useMemo(() => {
    if (rangeStartMs == null) return playbackStartIso;
    return wallIsoFromMs(rangeStartMs + seekValueSec * 1000);
  }, [playbackStartIso, rangeStartMs, seekValueSec]);

  useEffect(() => {
    if (open) { setStatus('connecting'); setMuted(true); setIsFullscreen(false); }
    if (!open) clearTransitionSnapshot();
  }, [clearTransitionSnapshot, open]);

  useEffect(() => {
    if (!open || !session?.id) return undefined;
    const raf = requestAnimationFrame(() => {
      playerRef.current?.setMuted?.(muted);
    });
    return () => cancelAnimationFrame(raf);
  }, [muted, open, session?.id]);

  useEffect(() => {
    return () => clearTransitionSnapshot();
  }, [clearTransitionSnapshot]);

  useEffect(() => {
    if (status === 'error') clearTransitionSnapshot();
  }, [clearTransitionSnapshot, status]);

  const setFreshTransitionSnapshot = useCallback((url) => {
    if (!url) return;
    setTransitionSnapshotUrl((prev) => {
      if (prev?.startsWith?.('blob:') && typeof URL !== 'undefined') URL.revokeObjectURL(prev);
      return url;
    });
  }, []);

  useEffect(() => {
    if (!open || rangeStartMs == null || playbackStartMs == null) return;
    const startOffsetSec = clampNumber(Math.round((playbackStartMs - rangeStartMs) / 1000), 0, durationSec || 0);
    playbackClockRef.current = { sessionId: session?.id, startedAt: Date.now(), startOffsetSec };
    setSeekValueSec(startOffsetSec);
    setIsDraggingSeek(false);
    setIsSeeking(false);
  }, [durationSec, open, playbackStartMs, rangeStartMs, session?.id]);

  useEffect(() => {
    if (!open || isDraggingSeek || isSeeking || status !== 'playing' || durationSec <= 0) return undefined;
    const timer = setInterval(() => {
      const base = playbackClockRef.current;
      const elapsedSec = Math.floor((Date.now() - base.startedAt) / 1000);
      setSeekValueSec(clampNumber(base.startOffsetSec + elapsedSec, 0, durationSec));
    }, 1000);
    return () => clearInterval(timer);
  }, [durationSec, isDraggingSeek, isSeeking, open, status]);

  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('fullscreenchange', onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);

  // Closing the modal (X, ESC, or programmatic) should ALWAYS stop the
  // device — user decision in iter145.  onOpenChange is the single
  // source of truth for "modal is closing".
  const handleOpenChange = (next) => {
    if (!next) {
      // Don't wait for the stop RPC to resolve — close UI immediately.
      try { onStop?.(); } catch { /* noop */ }
      onClose?.();
    }
  };

  const handleMute = async () => {
    const next = !muted;
    await playerRef.current?.setMuted?.(next);
    setMuted(next);
  };
  const handleFs = () => {
    // Fullscreen the OUTER wrapper that contains the video + header +
    // controls so the Stop / Mute / Minimize buttons remain visible.
    const el = modalContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const commitSeek = async () => {
    if (!onSeek || isSeeking || durationSec <= 0 || rangeStartMs == null) return;
    const targetOffsetSec = clampNumber(Math.round(seekValueSec), 0, durationSec);
    const targetIso = wallIsoFromMs(rangeStartMs + targetOffsetSec * 1000);
    const baseOffset = playbackClockRef.current.startOffsetSec || 0;
    if (Math.abs(targetOffsetSec - baseOffset) < 1 && status !== 'playing') return;
    setIsDraggingSeek(false);
    try {
      const snapshotUrl = await playerRef.current?.captureFrameSnapshot?.();
      setFreshTransitionSnapshot(snapshotUrl);
    } catch (_) { /* non-critical visual fallback */ }
    setIsSeeking(true);
    setStatus('connecting');
    try {
      await onSeek(targetIso, { offsetSeconds: targetOffsetSec });
    } finally {
      setIsSeeking(false);
    }
  };

  const createPlaybackWsUrl = useCallback(async () => {
    const apiBase = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = apiBase.replace(/^http/, 'ws');
    const imei = session?.device_imei || '';
    const channel = Number(session?.channel || 1);
    if (!imei || !session?.device_id || !channel) throw new Error('Playback session missing device details');
    const tokenRes = await api.post('/media/native-playback/token', {
      device_id: session.device_id,
      channel,
      session_id: session.id,
    });
    const streamToken = tokenRes.data?.stream_token;
    if (!streamToken) throw new Error('Playback stream token unavailable');
    const path = session.native_ws_path || `/api/media/native-playback/${imei}/${channel}/stream`;
    return `${wsBase}${path}?stream_token=${encodeURIComponent(streamToken)}`;
  }, [api, session?.device_id, session?.device_imei, session?.channel, session?.id, session?.native_ws_path]);

  if (!session) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-4xl p-0 overflow-hidden bg-black"
        data-testid="remote-playback-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        {/* Visually hidden title — required by Radix Dialog for
            screen-reader accessibility.  The visible title is the <h3>
            inside the gradient header below. */}
        <DialogTitle className="sr-only">
          {`Remote Playback — ${channelLabel || (session.channel != null ? `CH${session.channel}` : '')}${deviceName ? ` — ${deviceName}` : ''}`}
        </DialogTitle>
        <div ref={modalContainerRef} className="relative bg-black">
          {/* Header — same pointer-events gymnastics as VideoPlayerModal
              so the shadcn built-in X at top-right remains clickable. */}
          <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4 pr-12 pointer-events-none select-text">
            <div className="pointer-events-auto">
              <h3 className="text-white font-semibold text-lg truncate" data-testid="remote-playback-modal-title">
                Remote Playback — {channelLabel || (session.channel != null ? `CH${session.channel}` : '')}
              </h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <Badge className="bg-emerald-600 text-white text-xs" data-testid="remote-playback-channel-label">
                  {channelLabel || `CH${session.channel}`}
                </Badge>
                {channelLabel && session.channel != null && (
                  <Badge variant="outline" className="text-white/70 border-white/30 text-[10px]">
                    CH{session.channel}
                  </Badge>
                )}
                <Badge className="bg-slate-600 text-white text-xs" data-testid="remote-playback-mode-badge">Playback</Badge>
                {deviceName && <span className="text-white/70 text-sm truncate">{deviceName}</span>}
                {segment && (
                  <span className="text-white/60 text-xs truncate">
                    {formatWallClock(segment.start_time)} → {formatWallClock(segment.end_time, { includeDate: false })}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Body — the native WebCodecs player fills a 16:9 area */}
          <div className="relative aspect-video bg-black">
            <NativeWebCodecsPlayer
              key={session?.id || 'remote-playback'}
              ref={playerRef}
              wsUrlFactory={createPlaybackWsUrl}
              onStatusChange={setStatus}
              onFirstVisibleFrame={clearTransitionSnapshot}
              label={`Playback ${channelLabel || `CH${session.channel}`}`}
              startupText="Starting remote playback"
            />
            {transitionSnapshotUrl && (
              <div className="absolute inset-0 bg-black" data-testid="remote-playback-transition-snapshot-layer">
                <img
                  src={transitionSnapshotUrl}
                  alt="Last visible remote playback frame while seeking"
                  className="h-full w-full object-contain transition-opacity duration-200"
                  data-testid="remote-playback-transition-snapshot"
                />
                <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded bg-black/55 px-3 py-1.5 text-xs font-medium text-white shadow-lg backdrop-blur-sm" data-testid="remote-playback-transition-seeking-indicator">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Seeking…
                </div>
              </div>
            )}
          </div>

          {/* Controls bar — seek, mute, fullscreen, stop */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 via-black/55 to-transparent px-2 pb-2 pt-5">
            <div className="mb-1 rounded bg-black/35 px-2 py-1 backdrop-blur-sm" data-testid="remote-playback-seek-panel">
              <div className="mb-0.5 flex items-center justify-between gap-2 text-[10px] leading-none text-white/70">
                <span data-testid="remote-playback-seek-current-label">{formatWallClock(currentSeekIso, { includeDate: false })}</span>
                <span className="text-white/45" data-testid="remote-playback-seek-action-label">
                  {isSeeking ? 'Seeking…' : isDraggingSeek ? 'Release to jump' : ''}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max={Math.max(1, durationSec)}
                step="1"
                value={clampNumber(seekValueSec, 0, Math.max(1, durationSec))}
                disabled={isSeeking || durationSec <= 1}
                onChange={(event) => setSeekValueSec(parseInt(event.target.value, 10) || 0)}
                onPointerDown={() => setIsDraggingSeek(true)}
                onPointerUp={commitSeek}
                onKeyUp={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') commitSeek();
                }}
                onBlur={() => { if (isDraggingSeek) commitSeek(); }}
                className="h-3 w-full accent-emerald-400 cursor-pointer disabled:cursor-not-allowed"
                aria-label="Seek remote playback position"
                data-testid="remote-playback-seek-slider"
              />
              <div className="mt-0.5 grid grid-cols-[4.5rem_1fr_4.5rem] items-center text-[10px] leading-none text-white/45 font-mono">
                <span data-testid="remote-playback-seek-start-label">{clockSecondsLabel(0)}</span>
                <span className="text-center text-white/60" data-testid="remote-playback-seek-range-label">
                  {durationLabel(rangeStartIso, rangeEndIso)} · {formatWallClock(rangeStartIso, { includeDate: false })} → {formatWallClock(rangeEndIso, { includeDate: false })}
                </span>
                <span className="text-right" data-testid="remote-playback-seek-end-label">{clockSecondsLabel(durationSec)}</span>
              </div>
            </div>
            <div className="flex items-center justify-between px-1">
              <div className="flex items-center gap-2 text-white/80 text-xs">
                <span className={`inline-block h-2 w-2 rounded-full ${status === 'playing' && !isSeeking ? 'bg-emerald-400' : status === 'error' ? 'bg-red-400' : 'bg-yellow-400 animate-pulse'}`} />
                <span data-testid="remote-playback-status-label">
                  {isSeeking ? 'Seeking…' : status === 'playing' ? 'Streaming' : status === 'error' ? 'Error' : status === 'retrying' ? 'Reconnecting…' : 'Connecting…'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost" size="icon"
                  className="text-white hover:bg-white/20 h-7 w-7"
                  onClick={handleMute}
                  title={muted ? 'Unmute' : 'Mute'}
                  data-testid="remote-playback-mute-button"
                >
                  {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost" size="icon"
                  className="text-white hover:bg-white/20 h-7 w-7"
                  onClick={handleFs}
                  title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                  data-testid="remote-playback-fullscreen-button"
                >
                  {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleOpenChange(false)}
                  disabled={isSeeking}
                  className="ml-1 h-7 gap-1.5 bg-red-600 px-2 text-xs text-white hover:bg-red-700"
                  data-testid="remote-media-stop-playback-button"
                >
                  <Square className="h-3.5 w-3.5" /> Stop
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Segment Info modal — opens when the user clicks a segment bar on the
 * timeline graph (iter146 redesign).
 *
 * Shows the same metadata the List view shows (channel, time range,
 * duration, device-media UTC, type, stream type, file size) and offers
 * two primary actions:
 *   - **Stream**   → native A9D remote playback
 *   - **Download** → A9F file download (Mode A — alarm event_code filter, or Mode B — whole-clip range)
 *
 * Replaces the previous behaviour where clicking a graph segment would
 * immediately start remote playback with no confirmation, which was easy
 * to do by accident and gave the user no chance to choose download
 * instead.
 */
const SegmentInfoModal = ({ open, onOpenChange, segment, channelLabel, onStream, onDownload, downloadLoading }) => {
  if (!segment) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="segment-info-modal">
        <DialogHeader>
          <DialogTitle>
            <span className="flex items-center gap-2">
              <Badge variant="outline" data-testid="segment-info-channel-label">
                {channelLabel || `CH${segment.channel}`}
              </Badge>
              <span>Segment Details</span>
            </span>
          </DialogTitle>
          <DialogDescription>
            Choose how you want to consume this clip from the device&apos;s SD card.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="rounded-md border bg-slate-50 p-3 dark:bg-slate-900/40 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Time (display TZ)</span>
              <span className="font-medium">{formatWallClock(segment.start_time)} → {formatWallClock(segment.end_time, { includeDate: false })}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Duration</span>
              <span className="font-medium">{durationLabel(segment.start_time, segment.end_time)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Device media (UTC)</span>
              <span className="font-mono text-[11px]">{formatWallClock(segment.media_start_time)} → {formatWallClock(segment.media_end_time, { includeDate: false })}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Type</span>
              <Badge
                className={segment.video_type === 'alarm' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}
                data-testid="segment-info-type-badge"
              >
                {segment.video_type === 'alarm'
                  ? `Alarm — ${segment.event_name || `Event ${segment.event_code}`} (${segment.event_code})`
                  : 'Normal'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Stream</span>
              <span className="font-medium capitalize">{segment.stream_type}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">File size</span>
              <span className="font-medium">{segment.file_size_display || '-'}</span>
            </div>
            {segment.id && (
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">File ID</span>
                <span className="font-mono text-[11px]">{segment.id}</span>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="segment-info-cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={() => { onOpenChange(false); onDownload(segment); }}
            disabled={downloadLoading}
            className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="segment-info-download-button"
          >
            {downloadLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download
          </Button>
          <Button
            onClick={() => { onOpenChange(false); onStream(segment); }}
            className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="segment-info-stream-button"
          >
            <Play className="h-4 w-4" /> Stream
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const RemoteDownloads = () => {
  const { api, token } = useAuth();
  const { connected: _mediaWsConnected, subscribeToCommand } = useMediaWebSocket(token);
  // Direct Consume from MediaContext — these survive sub-tab switches and page navigation
  const {
    rdSelectedDevice: selectedDevice,
    setRdSelectedDevice: setSelectedDevice,
    rdMonthDateIso,
    setRdMonthDateIso,
    rdSelectedDateIso,
    setRdSelectedDateIso,
    rdStartTime: startTime,
    setRdStartTime: setStartTime,
    rdEndTime: endTime,
    setRdEndTime: setEndTime,
    rdStorageType: storageType,
    setRdStorageType: setStorageType,
    rdStreamType: streamType,
    setRdStreamType: setStreamType,
    rdVideoType: videoType,
    setRdVideoType: setVideoType,
    rdSelectedChannels,
    setRdSelectedChannels,
  } = useMedia();

  const [devices, setDevices] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [totalDownloads, setTotalDownloads] = useState(0);
  const [loadingDownloads, setLoadingDownloads] = useState(true);
  const [downloadsPage, setDownloadsPage] = useUrlPage('dl_page', { zeroIndexed: true });
  const [downloadsPageSize, setDownloadsPageSize] = useUrlPageSize('remote-media-download', 10, 'dl_size');
  const [downloadsSearch, setDownloadsSearch] = useState('');
  const [downloadsSearchDebounced, setDownloadsSearchDebounced] = useState('');
  const [downloadsDateRange, setDownloadsDateRange] = useState({ from: undefined, to: undefined });
  const [downloadsDateResetKey, setDownloadsDateResetKey] = useState(0);
  const [deviceSearchQuery, setDeviceSearchQuery] = useState('');
  const [deviceDropdownOpen, setDeviceDropdownOpen] = useState(false);

  // Local Date objects hydrated from persisted ISO strings; setters mirror back to context
  const [monthDate, setMonthDateLocal] = useState(() => hydrateDate(rdMonthDateIso, new Date()));
  const [selectedDate, setSelectedDateLocal] = useState(() => hydrateDate(rdSelectedDateIso, new Date()));
  const setMonthDate = useCallback((d) => {
    setMonthDateLocal(d);
    setRdMonthDateIso(serializeDate(d));
  }, [setRdMonthDateIso]);
  const setSelectedDate = useCallback((d) => {
    setSelectedDateLocal(d);
    setRdSelectedDateIso(serializeDate(d));
  }, [setRdSelectedDateIso]);

  // selectedChannels is a Set locally (existing consumers depend on Set semantics);
  // mirrors to context as an Array via wrapped setter
  const [selectedChannels, setSelectedChannelsLocal] = useState(() => new Set(rdSelectedChannels || []));
  const setSelectedChannels = useCallback((next) => {
    setSelectedChannelsLocal((prev) => {
      const resolved = typeof next === 'function' ? next(prev) : next;
      const asSet = resolved instanceof Set ? resolved : new Set(resolved || []);
      return asSet;
    });
  }, []);

  useEffect(() => {
    setRdSelectedChannels([...selectedChannels]);
  }, [selectedChannels, setRdSelectedChannels]);

  const [calendarDays, setCalendarDays] = useState({});
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [segments, setSegments] = useState([]);
  // iter146: clicking a segment bar in the timeline graph used to fire
  // handleStartPlayback() directly. Now it opens a SegmentInfoModal so
  // the user can review metadata + choose Stream vs Download.
  const [segmentInfoTarget, setSegmentInfoTarget] = useState(null);
  const [listExpanded, setListExpanded] = useState(false);
  const [segmentListPage, setSegmentListPage] = useState(1);
  const [segmentListPageSize, setSegmentListPageSize] = usePersistedPageSize('remote-media-list', 10);
  const [activePlayback, setActivePlayback] = useState(null);
  const [activePlaybackSegment, setActivePlaybackSegment] = useState(null);
  const [downloadLoadingId, setDownloadLoadingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchParsedCount, setSearchParsedCount] = useState(0);
  const [conversionStatusById, setConversionStatusById] = useState({});
  const [downloadFileLoadingId, setDownloadFileLoadingId] = useState(null);
  const [timelineHover, setTimelineHover] = useState(null);
  const calendarQueryKeyRef = useRef(null);
  const calendarRequestSeqRef = useRef(0);
  const deviceSearchInputRef = useRef(null);
  const conversionPollTimersRef = useRef({});
  const conversionPollCancelledRef = useRef({});

  useEffect(() => () => {
    Object.values(conversionPollTimersRef.current).forEach(clearTimeout);
    conversionPollTimersRef.current = {};
  }, []);

  useEffect(() => {
    if (!Object.keys(conversionStatusById).length) return;
    const completedIds = downloads
      .filter(dl => conversionStatusById[dl.id] && ['ready', 'failed'].includes(dl.playback_status))
      .map(dl => dl.id);
    if (!completedIds.length) return;
    completedIds.forEach(id => {
      if (conversionPollTimersRef.current[id]) {
        clearTimeout(conversionPollTimersRef.current[id]);
        delete conversionPollTimersRef.current[id];
      }
      delete conversionPollCancelledRef.current[id];
    });
    setConversionStatusById(prev => {
      const next = { ...prev };
      completedIds.forEach(id => { delete next[id]; });
      return next;
    });
  }, [conversionStatusById, downloads]);

  useEffect(() => {
    if (!deviceDropdownOpen) return;
    const id = requestAnimationFrame(() => deviceSearchInputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [deviceDropdownOpen]);
  // iter148 — Timeline zoom window (seconds-of-day). Default = full 24h.
  // User explicitly asked for full-day default; `Reset zoom` restores this.
  const [zoomStart, setZoomStart] = useState(0);
  const [zoomEnd, setZoomEnd] = useState(86400);
  const [zoomDrag, setZoomDrag] = useState(null); // 'left' | 'right' | 'window' | null
  const zoomDragRef = useRef(null);
  // iter148 — Calendar month-switch rate limiter. Mirrors `calendarLoading`
  // but with a hard 15s watchdog so the Prev/Next buttons can't stay locked
  // forever if the AA4 reply never lands.
  const [monthLoading, setMonthLoading] = useState(false);

  // Video player
  const [playerOpen, setPlayerOpen] = useState(false);
  const [playerVideo, setPlayerVideo] = useState(null);
  const [playerUrl, setPlayerUrl] = useState(null);

  // Custom Date download dialog (iter143 redesign).
  const [customDialogOpen, setCustomDialogOpen] = useState(false);

  // Device map for timezone lookup in the downloads table.
  const deviceMap = useMemo(
    () => Object.fromEntries(devices.map(d => [d.id, d])),
    [devices],
  );
  const selectedDeviceObj = useMemo(() => devices.find(d => d.id === selectedDevice), [devices, selectedDevice]);
  const selectedDeviceIsMeitrack = useMemo(() => isMeitrackVideoDevice(selectedDeviceObj), [selectedDeviceObj]);
  const filteredDevices = useMemo(() => {
    const query = deviceSearchQuery.trim().toLowerCase();
    return [...devices]
      .sort((a, b) => deviceDisplayName(a).localeCompare(deviceDisplayName(b), undefined, { sensitivity: 'base' }))
      .filter((device) => {
        return !query || [deviceDisplayName(device), device.name, device.imei]
          .some(value => String(value || '').toLowerCase().includes(query));
      });
  }, [devices, deviceSearchQuery]);

  useEffect(() => {
    if (selectedDeviceIsMeitrack && storageType !== '0') {
      setStorageType('0');
    }
  }, [selectedDeviceIsMeitrack, storageType, setStorageType]);

  const handleStorageTypeChange = useCallback((value) => {
    setStorageType(selectedDeviceIsMeitrack ? '0' : value);
  }, [selectedDeviceIsMeitrack, setStorageType]);

  const selectedTz = resolveTimezone({ device: selectedDeviceObj });
  const mediaTz = selectedDeviceObj?.media_timezone || selectedDeviceObj?.video_storage_timezone || 'UTC';
  const maxSegmentChannel = useMemo(
    () => segments.reduce((max, s) => Math.max(max, Number(s.channel || 0)), 0),
    [segments],
  );
  const channelList = useMemo(() => {
    if (!selectedDeviceObj) return [];
    const configured = Number(selectedDeviceObj.total_cameras || selectedDeviceObj.connected_cameras || 0);
    const namedCount = Object.keys(selectedDeviceObj.channel_names || {}).length;
    const count = Math.max(1, configured || namedCount || maxSegmentChannel || 1);
    return Array.from({ length: count }, (_, idx) => {
      const ch = idx + 1;
      return { id: ch, label: selectedDeviceObj.channel_names?.[String(ch)] || `CH${ch}` };
    });
  }, [selectedDeviceObj, maxSegmentChannel]);
  const channelLabelMap = useMemo(
    () => Object.fromEntries(channelList.map(ch => [ch.id, ch.label])),
    [channelList],
  );

  useEffect(() => {
    setSelectedChannels(new Set(channelList.map(c => c.id)));
  }, [channelList, setSelectedChannels]);

  const fetchDevices = useCallback(async () => {
    try {
      const res = await api.get('/media/video-devices');
      setDevices(res.data);
    } catch (e) {
      console.error('Failed to fetch devices:', e);
    }
  }, [api]);

  const fetchDownloads = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.set('skip', String(downloadsPage * downloadsPageSize));
      params.set('limit', String(downloadsPageSize));
      if (downloadsSearchDebounced) params.set('search', downloadsSearchDebounced);
      if (downloadsDateRange.from) params.set('from_date', downloadsDateRange.from.toISOString());
      if (downloadsDateRange.to) {
                params.set('to_date', downloadsDateRange.to.toISOString());
      }
      const res = await api.get(`/media/remote/downloads?${params.toString()}`);
      setDownloads(res.data.items || []);
      setTotalDownloads(res.data.total || 0);
    } catch (e) {
      console.error('Failed to fetch downloads:', e);
    } finally {
      setLoadingDownloads(false);
    }
  }, [api, downloadsPage, downloadsPageSize, downloadsSearchDebounced, downloadsDateRange]);

  // Debounce the download search input to avoid a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDownloadsSearchDebounced(downloadsSearch.trim()), 300);
    return () => clearTimeout(handle);
  }, [downloadsSearch]);

  // Reset to page 1 whenever the search / date filter / page size changes
  // so the user always sees the first matches instead of an empty page.
  // useSkipFirstEffect (value-compared deps, no unstable setter dep): a raw
  // useEffect with setDownloadsPage in deps re-fired on every URL change
  // (react-router setSearchParams identity), snapping page 2 back to page 1.
  useSkipFirstEffect(() => {
    setDownloadsPage(0);
  }, [downloadsSearchDebounced, downloadsPageSize, downloadsDateRange]);

  useEffect(() => {
    fetchDevices();
    fetchDownloads();
    const interval = setInterval(fetchDownloads, 15000);
    return () => clearInterval(interval);
  }, [fetchDevices, fetchDownloads]);

  // Cleanup in-flight search when RemoteDownloads unmounts so the backend
  // lock is freed and stale polling stops.
  useEffect(() => {
    return () => {
      const active = activeSearchRef.current;
      if (active?.abort) {
        try { active.abort.abort(); } catch { /* ignore */ }
      }
    };
  }, []);

  const pollUntilDone = useCallback(async (url, onComplete, onProgress, commandId = null) => {
    // WS integration: when a Meitrack packet arrives for any of the
    // supplied command ids, resolve the sleep immediately instead of
    // waiting the full poll interval. This gives sub-second perceived
    // latency while keeping the tried-and-true GET-based merging logic
    // on the backend as source of truth.
    //
    // iter150 — accept either a single command id (legacy callers) or
    // an array of ids (calendar-query fan-out, where 3 AA4 commands are
    // queued back-to-back and any of them landing should wake the poll).
    let wakeUp = null;
    const commandIds = Array.isArray(commandId)
      ? commandId.filter(Boolean)
      : (commandId ? [commandId] : []);
    const unsubs = commandIds.map((cid) => subscribeToCommand(cid, () => {
      if (wakeUp) { wakeUp(); wakeUp = null; }
    }));
    try {
      for (let i = 0; i < 40; i += 1) {
        const res = await api.get(url);
        onProgress?.(res.data);
        if (res.data.status === 'completed') { onComplete(res.data); return res.data; }
        if (res.data.status === 'failed') throw new Error(res.data.error || 'Device query failed');
        await new Promise(resolve => {
          wakeUp = resolve;
          setTimeout(resolve, 3000);
        });
      }
      throw new Error('Timed out waiting for device response');
    } finally {
      unsubs.forEach((u) => { try { u(); } catch { /* ignore */ } });
    }
  }, [api, subscribeToCommand]);

  const [calendarCached, setCalendarCached] = useState(null);  // {at: iso} when served from cache

  // iter150 — Keep a ref to the always-current devices array so
  // `handleQueryCalendar` and the auto-trigger useEffect below don't
  // need `devices` in their dependency arrays. Without this, every 15s
  // device refetch produced a new array reference, re-creating the
  // callback and re-running the effect — which in turn fired duplicate
  // AA4 calendar queries and contributed to the MD300 command_id
  // mis-attribution issue.
  const devicesRef = useRef(devices);
  useEffect(() => { devicesRef.current = devices; }, [devices]);
  // First-time devices-arrival sentinel. We want the auto-query to run
  // once after the device list first populates, but we DON'T want it
  // re-running every poll cycle.
  const devicesLoaded = devices.length > 0;

  const handleQueryCalendar = useCallback(async (silent = false, deviceOverride = selectedDevice, monthOverride = monthDate, forceRefresh = false) => {
    if (!deviceOverride) { if (!silent) toast.error('Please select a device'); return; }
    // iter148.1 — skip AA4 entirely if the selected device is offline. The
    // AA4 command would just queue up forever waiting for a response and
    // wedge the rate-limiter for `monthLoading`'s full 10s on every month
    // change. Surface that to the user instead of silently spinning.
    const targetDevice = devicesRef.current.find(d => d.id === deviceOverride);
    if (targetDevice && targetDevice.is_online === false) {
      if (!silent) toast.error('Device is offline — calendar query skipped');
      return;
    }
    const manualRefreshWithVideoModuleOff = targetDevice && !silent && !getCameraRecordingState(targetDevice).hasAnyRecording;
    if (manualRefreshWithVideoModuleOff) {
      toast.warning('Device online but video module off', { id: VIDEO_MODULE_OFF_TOAST_ID });
    }
    const queryKey = `${deviceOverride}-${monthOverride.getFullYear()}-${monthOverride.getMonth() + 1}-${forceRefresh ? 'fresh' : 'cache'}`;
    if (calendarQueryKeyRef.current === queryKey) return;
    calendarQueryKeyRef.current = queryKey;
    const requestSeq = calendarRequestSeqRef.current + 1;
    calendarRequestSeqRef.current = requestSeq;
    const isLatestCalendarRequest = () => requestSeq === calendarRequestSeqRef.current;
    setCalendarLoading(true);
    try {
      // Don't wipe calendarDays eagerly here — it causes a flash on cache hits.
      // Device-change is already cleared by the device Select's onValueChange,
      // and month-change re-populates via the response. If the POST returns
      // a cache miss below we'll still overwrite with the AA4 result.
      const res = await api.post('/media/remote-media/calendar-query', {
        device_id: deviceOverride,
        year: monthOverride.getFullYear(),
        month: monthOverride.getMonth() + 1,
        display_timezone: selectedTz,
        media_timezone: mediaTz,
        force_refresh: forceRefresh,
      });
      // Cache hit → server returned completed synchronously.
      if (res.data?.status === 'completed' && Array.isArray(res.data?.days)) {
        if (!isLatestCalendarRequest()) return;
        const mapped = Object.fromEntries(res.data.days.map(d => [d.date, d]));
        setCalendarDays(mapped);
        setCalendarCached({ at: res.data.cached_at || null });
        const count = res.data.days.filter(d => d.has_any).length;
        if (!silent && !manualRefreshWithVideoModuleOff) toast.success(`Calendar loaded from cache: ${count} day${count === 1 ? '' : 's'} with video`);
        return;
      }
      // Cache miss → only wipe existing days if they belong to a DIFFERENT month.
      // Same-month re-queries (triggered by cache expiry or harmless useEffect
      // re-fires from devices-list refetches) keep the previous bitmap visible
      // until the new AA4 reply arrives, avoiding the "red/green disappears
      // while the device is responding" flash the user reported.
      if (!isLatestCalendarRequest()) return;
      const targetMonthPrefix = `${monthOverride.getFullYear()}-${String(monthOverride.getMonth() + 1).padStart(2, '0')}-`;
      setCalendarDays(prev => {
        const keys = Object.keys(prev);
        const sameMonth = keys.length > 0 && keys.every(k => k.startsWith(targetMonthPrefix));
        return sameMonth ? prev : {};
      });
      setCalendarCached(null);
      if (!silent && !manualRefreshWithVideoModuleOff) toast.info('Calendar query sent to device');
      const data = await pollUntilDone(`/media/remote-media/calendar-query/${res.data.query_id}`, (done) => {
        if (!isLatestCalendarRequest()) return;
        const mapped = Object.fromEntries((done.days || []).map(d => [d.date, d]));
        setCalendarDays(mapped);
      }, null, res.data.command_ids || res.data.command?.id);
      if (!isLatestCalendarRequest()) return;
      const count = (data.days || []).filter(d => d.has_any).length;
      if (!silent && !manualRefreshWithVideoModuleOff) {
        if (data.partial) {
          toast.warning(`Calendar loaded (partial): ${count} day${count === 1 ? '' : 's'} with video — some months still loading from device`);
        } else {
          toast.success(`Calendar loaded: ${count} day${count === 1 ? '' : 's'} with video`);
        }
      }
    } catch (e) {
      if (!isLatestCalendarRequest()) return;
      if (!silent) toast.error(e.response?.data?.detail || e.message || 'Failed to query calendar');
      else console.warn('Auto calendar query failed:', e);
    } finally {
      if (isLatestCalendarRequest()) setCalendarLoading(false);
      if (calendarQueryKeyRef.current === queryKey) calendarQueryKeyRef.current = null;
    }
  }, [api, selectedDevice, monthDate, pollUntilDone, selectedTz, mediaTz]);

  useEffect(() => {
    if (!devicesLoaded) return;
    const targetDevice = devicesRef.current.find(d => d.id === selectedDevice);
    if (selectedDevice && targetDevice && getCameraRecordingState(targetDevice).hasAnyRecording) {
      handleQueryCalendar(true, selectedDevice, monthDate);
    }
  }, [selectedDevice, monthDate, handleQueryCalendar, devicesLoaded]);

  // iter148 — month-switch watchdog. When `calendarLoading` flips true we
  // mirror it onto `monthLoading` (used to disable < Prev / Next > calendar
  // buttons) but auto-clear after 10 seconds so the buttons never stay
  // locked if the AA4 reply is dropped by the device.
  // iter148.1 — drop 15s → 10s (per user). Rate-limiter only takes effect
  // when a device is selected (otherwise the user must be free to flip
  // months without waiting for any backend round-trip).
  useEffect(() => {
    if (!calendarLoading) { setMonthLoading(false); return undefined; }
    setMonthLoading(true);
    const timer = setTimeout(() => setMonthLoading(false), 10000);
    return () => clearTimeout(timer);
  }, [calendarLoading]);

  // Custom polling for AB8 search — slow GPRS links can deliver packets
  // over 2-4 minutes for large multi-packet responses. The backend owns
  // the session lifecycle (per-device MediaSessionManager lock), so the
  // frontend just polls the session endpoint until status is terminal.
  //
  // When a commandId is provided, we also open a Media WebSocket for it so
  // each arriving Meitrack packet wakes up the poll loop immediately
  // (sub-second) instead of waiting the full 3s/5s interval.
  const pollSearchUntilFinal = useCallback(async (url, onProgress, commandId = null, abortSignal = null) => {
    const MAX_PENDING_ITERATIONS = 100;   // up to 5 min of pending polling (3s each)
    let wakeUp = null;
    let wsUnsub = () => {};
    if (commandId) {
      wsUnsub = subscribeToCommand(commandId, () => {
        if (wakeUp) { wakeUp(); wakeUp = null; }
      });
    }
    const abortWaker = () => { if (wakeUp) { wakeUp(); wakeUp = null; } };
    abortSignal?.addEventListener?.('abort', abortWaker);
    try {
      for (let i = 0; i < MAX_PENDING_ITERATIONS; i += 1) {
        if (abortSignal?.aborted) {
          const err = new Error('Search cancelled');
          err.name = 'AbortError';
          throw err;
        }
        const res = await api.get(url, { signal: abortSignal || undefined });
        onProgress?.(res.data);
        const status = res.data.status;
        if (status === 'failed' || status === 'timed_out') {
          throw new Error(res.data.error || res.data.warning || 'Device query failed');
        }
        if (status === 'cancelled') {
          const err = new Error(res.data.warning || 'Search cancelled on the server');
          err.name = 'AbortError';
          throw err;
        }
        if (status === 'complete') {
          return res.data;
        }
        // status === 'collecting' → wait and poll again.
        await new Promise(r => {
          wakeUp = r;
          setTimeout(r, 3000);
        });
      }
      throw new Error('Timed out waiting for device response');
    } finally {
      abortSignal?.removeEventListener?.('abort', abortWaker);
      wsUnsub();
    }
  }, [api, subscribeToCommand]);

  // Keeps track of the in-flight search request so late responses from a
  // previous day-click don't overwrite the UI of the current day.  When
  // the user clicks a different day mid-search, the old AbortController
  // is aborted, the new request gets a new ref, and any pending poll
  // from the old search is dropped on the floor.
  const activeSearchRef = useRef({ abort: null, requestId: null });

  const handleCancelActiveSearch = useCallback(async () => {
    const active = activeSearchRef.current;
    if (active.abort) {
      try { active.abort.abort(); } catch { /* ignore */ }
    }
    if (active.requestId) {
      try {
        await api.post(`/media/remote-media/search/${active.requestId}/cancel`);
      } catch (e) {
        console.debug('Cancel of previous search failed (ok if server had already freed the lock):', e.message);
      }
    }
    activeSearchRef.current = { abort: null, requestId: null };
  }, [api]);

  const selectRemoteMediaDevice = useCallback((value) => {
    calendarRequestSeqRef.current += 1;
    handleCancelActiveSearch();
    setSelectedDevice(value);
    setDeviceSearchQuery('');
    setDeviceDropdownOpen(false);
    setCalendarDays({});
    setSegments([]);
    setActivePlayback(null);
    setActivePlaybackSegment(null);
    setStorageType('0');
    setZoomStart(0);
    setZoomEnd(86400);
  }, [handleCancelActiveSearch, setSelectedDevice, setStorageType]);

  const clearRemoteMediaDeviceSelection = useCallback((event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    selectRemoteMediaDevice('');
    setCalendarCached(null);
    setMonthLoading(false);
    setCalendarLoading(false);
    setDeviceDropdownOpen(false);
  }, [selectRemoteMediaDevice]);

  const handleSearchSegments = async () => {
    if (!selectedDevice) { toast.error('Please select a device'); return; }
    if (!selectedDate) { toast.error('Please select a day'); return; }
    const targetDevice = devices.find(d => d.id === selectedDevice);
    if (targetDevice?.is_online === false) {
      toast.error('Device is offline — video search skipped');
      return;
    }
    if (targetDevice && !getCameraRecordingState(targetDevice).hasAnyRecording) {
      toast.warning('Device online but video module off', { id: VIDEO_MODULE_OFF_TOAST_ID });
      return;
    }

    // Cancel any in-flight search BEFORE starting the new one.
    await handleCancelActiveSearch();

    setSearchLoading(true);
    const controller = new AbortController();
    const uiRequestToken = `${selectedDevice}:${dateKey(selectedDate)}:${startTime}:${endTime}:${Date.now()}`;
    activeSearchRef.current = { abort: controller, requestId: null, token: uiRequestToken };

    // iter148 — brand-new search = fresh day, so restore the timeline zoom
    // window to full-day. The user can re-zoom after the first packets
    // arrive (zoom is preserved across subsequent packet arrivals).
    setZoomStart(0);
    setZoomEnd(86400);

    try {
      setSegments([]);
      setSearchParsedCount(0);
      let res;
      try {
        res = await api.post('/media/remote-media/search', {
          device_id: selectedDevice,
          channel: 0,
          start_time: localDayTimeToDeviceIso(selectedDate, startTime, [0, 0, 0]),
          end_time: localDayTimeToDeviceIso(selectedDate, endTime, [23, 59, 59]),
          storage_type: selectedDeviceIsMeitrack ? 0 : parseInt(storageType, 10),
          stream_type: streamType,
          video_type: videoType,
          display_timezone: selectedTz,
          media_timezone: mediaTz,
        }, { signal: controller.signal });
      } catch (err) {
        // 409 DEVICE_MEDIA_BUSY — surface actionable error with a hint.
        if (err.response?.status === 409) {
          const detail = err.response.data?.detail || {};
          toast.error(
            detail.message || 'Device is still loading media from another search. Please wait or cancel that search first.',
            { duration: 7000 },
          );
          return;
        }
        throw err;
      }

      const requestId = res.data.request_id;
      activeSearchRef.current = { ...activeSearchRef.current, requestId };

      toast.info(res.data.same_request ? 'Riding along with an in-flight search' : 'Video search sent to device');

      const data = await pollSearchUntilFinal(
        `/media/remote-media/search/${requestId}`,
        (progress) => {
          // Ignore progress updates from a stale request (user switched days).
          if (activeSearchRef.current.token !== uiRequestToken) return;
          // iter148.3 — Task C: buffer `files` server-side, only commit
          // them to `segments` state when status === 'complete' (handled
          // below). Skipping the incremental `setSegments(progress.files)`
          // here means the timeline renders the full result set in one
          // shot, matching MS06's batched UX. The user still sees live
          // progress via `searchParsedCount` and the
          // "Searching…" segment-count badge.
          setSearchParsedCount((progress.files || []).length);
        },
        res.data.command_id,
        controller.signal,
      );

      if (activeSearchRef.current.token !== uiRequestToken) return; // stale
      const foundFiles = data.files || [];
      setSegments(foundFiles);
      setSearchParsedCount(foundFiles.length);
      if (foundFiles.length > 0 && selectedDate) {
        const hasAlarm = foundFiles.some(f => f.video_type === 'alarm');
        const hasNormal = foundFiles.some(f => f.video_type !== 'alarm');
        const key = dateKey(selectedDate);
        setCalendarDays(prev => ({
          ...prev,
          [key]: {
            ...(prev[key] || { day: selectedDate.getDate(), date: key }),
            has_normal: hasNormal,
            has_alarm: Boolean(prev[key]?.has_alarm || hasAlarm),
            has_any: true,
            color: (prev[key]?.has_alarm || hasAlarm) ? 'red' : 'green',
          },
        }));
      }
      if (data.partial_results || data.warning) {
        toast.warning(
          data.warning || `Showing ${foundFiles.length} clip(s) — some packets didn't arrive. Try a smaller time window.`,
          { duration: 8000 },
        );
      } else {
        toast.success(`${foundFiles.length} video segment${foundFiles.length === 1 ? '' : 's'} found`);
      }
    } catch (e) {
      if (e.name === 'AbortError' || e.name === 'CanceledError' || e.code === 'ERR_CANCELED') {
        // Silent — user switched days or cancelled explicitly.
        return;
      }
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || e.message || 'Failed to search device videos');
    } finally {
      if (activeSearchRef.current.token === uiRequestToken) {
        activeSearchRef.current = { abort: null, requestId: null };
      }
      setSearchLoading(false);
    }
  };

  const handleCancelDownload = async (downloadId) => {
    try {
      const res = await api.post(`/media/remote/download/${downloadId}/cancel`);
      const message = res.data?.device_cancel_queued
        ? 'Download cancelled — stop request sent to the device'
        : res.data?.queued_command_cancelled
          ? 'Download cancelled before the device started uploading'
          : 'Download cancelled';
      toast.success(message);
      fetchDownloads();
    } catch (e) {
      toast.error('Failed to cancel download');
    }
  };

  const updateConversionStatus = useCallback((downloadId, patch) => {
    setConversionStatusById(prev => ({
      ...prev,
      [downloadId]: {
        ...(prev[downloadId] || {}),
        ...patch,
        updated_at: new Date().toISOString(),
      },
    }));
  }, []);

  const clearConversionStatus = useCallback((downloadId) => {
    if (conversionPollTimersRef.current[downloadId]) {
      clearTimeout(conversionPollTimersRef.current[downloadId]);
      delete conversionPollTimersRef.current[downloadId];
    }
    delete conversionPollCancelledRef.current[downloadId];
    setConversionStatusById(prev => {
      const next = { ...prev };
      delete next[downloadId];
      return next;
    });
  }, []);

  const cancelConversionPolling = useCallback((downloadId) => {
    if (!downloadId) return;
    conversionPollCancelledRef.current[downloadId] = true;
    if (conversionPollTimersRef.current[downloadId]) {
      clearTimeout(conversionPollTimersRef.current[downloadId]);
      delete conversionPollTimersRef.current[downloadId];
    }
  }, []);

  const buildDownloadVideo = useCallback((dl) => {
    const rowDevice = deviceMap[dl.device_id];
    const channelLabel = rowDevice?.channel_names?.[String(dl.channel)] || null;
    const titleChannel = channelLabel || `CH${dl.channel}`;
    return {
      event_label: `Remote Download — ${titleChannel}`,
      event_type: 'remote_download',
      channel: dl.channel,
      channel_label: channelLabel,
      channel_type_name: channelLabel,
      device_name: dl.device_name,
      device_id: dl.device_id,
      created_at: dl.created_at,
      start_time: dl.start_time,
      end_time: dl.end_time,
      download_id: dl.id,
      filename: dl.filename,
      file_size_display: dl.file_size ? humanFileSize(dl.file_size) : null,
      media_metadata: dl.media_metadata,
      codec: dl.codec,
      browser_playback: dl.browser_playback,
      requires_hevc_support: dl.requires_hevc_support,
      h264_fallback_ready: dl.h264_fallback_ready,
      h264_fallback_status: dl.h264_fallback_status,
      h264_fallback_message: dl.h264_fallback_message,
    };
  }, [deviceMap]);

  const pollDownloadPlaybackUrl = useCallback(async (dl, attempt = 0) => {
    if (conversionPollCancelledRef.current[dl.id]) return;
    try {
      const res = await api.get(`/media/remote/download/${dl.id}/play-url`, {
        params: { retry_conversion: attempt === 0 },
      });
      if (res.data?.url) {
        if (conversionPollCancelledRef.current[dl.id]) return;
        clearConversionStatus(dl.id);
        setPlayerUrl(res.data.url);
        setPlayerVideo(prev => (prev ? {
          ...prev,
          playback_status: 'ready',
          playback_message: 'Conversion complete',
          codec: res.data?.codec || prev.codec,
          browser_playback: res.data?.browser_playback || prev.browser_playback,
          requires_hevc_support: res.data?.requires_hevc_support ?? prev.requires_hevc_support,
          h264_fallback_ready: res.data?.h264_fallback_ready ?? prev.h264_fallback_ready,
          h264_fallback_status: res.data?.h264_fallback_status || prev.h264_fallback_status,
        } : buildDownloadVideo(dl)));
        setPlayerOpen(true);
        fetchDownloads();
        if (attempt > 0 || dl.filename?.toLowerCase().endsWith('.avmsg')) {
          toast.success('AVMSG conversion complete — opening video');
        }
        return;
      }

      if (['converting', 'queued'].includes(res.data?.playback_status) || ['converting', 'queued'].includes(res.data?.status)) {
        if (conversionPollCancelledRef.current[dl.id]) return;
        const message = res.data?.message || 'Converting AVMSG for browser playback';
        const status = res.data?.playback_status || res.data?.status || 'converting';
        updateConversionStatus(dl.id, { status, message, attempt, playback_queue_position: res.data?.playback_queue_position });
        setPlayerVideo(prev => ({
          ...(prev || buildDownloadVideo(dl)),
          playback_status: status,
          playback_message: message,
        }));
        setPlayerOpen(true);
        setPlayerUrl(null);
        const delay = Math.max(2, Number(res.data?.retry_after || 3)) * 1000;
        conversionPollTimersRef.current[dl.id] = setTimeout(() => {
          if (!conversionPollCancelledRef.current[dl.id]) {
            pollDownloadPlaybackUrl(dl, attempt + 1);
          }
        }, delay);
        return;
      }

      if (res.data?.playback_status === 'failed' || res.data?.status === 'failed') {
        throw new Error(res.data?.message || 'AVMSG conversion failed');
      }

      throw new Error(res.data?.message || 'Playback URL unavailable');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      const message = detail
        ? `Cannot play video${status ? ` (${status})` : ''}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`
        : `Failed to fetch video URL${status ? ` (${status})` : ''}`;
      updateConversionStatus(dl.id, { status: 'failed', message });
      setPlayerVideo(prev => (prev ? { ...prev, playback_status: 'failed', playback_message: message } : prev));
      console.error('[pollDownloadPlaybackUrl] error', { status, detail, dl });
      toast.error(message, { duration: 7000 });
      fetchDownloads();
    }
  }, [api, buildDownloadVideo, clearConversionStatus, fetchDownloads, updateConversionStatus]);

  const handlePlayDownload = async (dl) => {
    clearConversionStatus(dl.id);
    delete conversionPollCancelledRef.current[dl.id];
    setPlayerVideo(buildDownloadVideo(dl));
    setPlayerUrl(null);
    setPlayerOpen(true);
    if (dl.filename?.toLowerCase().endsWith('.avmsg') && !dl.playback_ready) {
      updateConversionStatus(dl.id, { status: 'converting', message: 'Starting AVMSG conversion…', attempt: 0 });
      toast.info('AVMSG conversion started. The video will open when it is ready.');
    }
    await pollDownloadPlaybackUrl(dl, 0);
  };

  const handlePlayerOpenChange = useCallback((nextOpen) => {
    setPlayerOpen(nextOpen);
    if (!nextOpen && playerVideo?.download_id && !playerUrl) {
      cancelConversionPolling(playerVideo.download_id);
    }
  }, [cancelConversionPolling, playerUrl, playerVideo?.download_id]);

  const handleConvertDownload = async (dl) => {
    clearConversionStatus(dl.id);
    delete conversionPollCancelledRef.current[dl.id];
    updateConversionStatus(dl.id, { status: 'converting', message: 'AVMSG conversion queued', attempt: 0 });
    try {
      const res = await api.post(`/media/remote/download/${dl.id}/convert`);
      const status = res.data?.playback_status || res.data?.status || 'converting';
      const message = res.data?.message || (status === 'ready' ? 'AVMSG ready' : 'Converting AVMSG for browser playback');
      updateConversionStatus(dl.id, { status, message, attempt: 0, playback_queue_position: res.data?.playback_queue_position });
      toast.success(status === 'ready' ? 'AVMSG is ready to play' : 'AVMSG conversion started');
      fetchDownloads();
      if (status !== 'ready') {
        const poll = (attempt = 1) => {
          conversionPollTimersRef.current[dl.id] = setTimeout(async () => {
            if (conversionPollCancelledRef.current[dl.id]) return;
            await fetchDownloads();
            if (attempt < 240 && !conversionPollCancelledRef.current[dl.id]) poll(attempt + 1);
          }, Math.max(3, Number(res.data?.retry_after || 5)) * 1000);
        };
        poll();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || 'Failed to start AVMSG conversion';
      updateConversionStatus(dl.id, { status: 'failed', message: typeof detail === 'string' ? detail : JSON.stringify(detail) });
      toast.error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    }
  };

  const handleDownloadFile = async (dl) => {
    setDownloadFileLoadingId(dl.id);
    try {
      const res = await api.get(`/media/remote/download/${dl.id}/play-url`, {
        params: { disposition: 'attachment', filename: dl.filename || 'remote-download' },
      });
      const url = res.data?.url;
      if (!url) throw new Error('No download URL available');
      const a = document.createElement('a');
      a.href = url;
      a.download = dl.filename || 'remote-download';
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Download started');
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      toast.error(detail
        ? `Cannot download file${status ? ` (${status})` : ''}: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`
        : 'Failed to prepare download');
    } finally {
      setDownloadFileLoadingId(null);
    }
  };

  /**
   * Start a native A9D playback.  On the "device already playing"
   * 409 response we auto-stop the current stream and retry once — users
   * found the manual "stop, then click play again" dance clunky
   * (iter144 UX fix).  The retry is opt-out: only fired when
   * ``_isRetry`` is falsy so we never loop forever.
   */
  const handleStartPlayback = async (segment, _isRetry = false) => {
    if (!selectedDevice) return;
    try {
      const res = await api.post('/media/remote-media/playback/start', {
        device_id: selectedDevice,
        channel: segment.channel,
        start_time: segment.start_time,
        end_time: segment.end_time,
        start_bcd: segment.start_bcd,
        end_bcd: segment.end_bcd,
        stream_type: segment.stream_type === 'all' ? streamType : segment.stream_type,
        resource_type: segment.resource_type || 0,
        memory_type: segment.memory_type_value ?? parseInt(storageType, 10),
        source_file_id: segment.id,
        media_timezone: mediaTz,
      });
      setActivePlayback(res.data.session);
      setActivePlaybackSegment(segment);
      toast.success(
        res.data.same_request
          ? `Joined active playback CH${segment.channel}`
          : `Starting remote playback CH${segment.channel}`
      );
    } catch (e) {
      // 409 DEVICE_MEDIA_BUSY — device is already playing another segment.
      // On first attempt auto-stop the active session and retry ONCE.
      // If the retry also errors (non-409), surface the error normally.
      if (e.response?.status === 409 && !_isRetry) {
        const detail = e.response.data?.detail || {};
        const toastId = toast.loading('Device already playing — stopping current playback and retrying…');
        try {
          await api.post('/media/remote-media/playback/stop', {
            device_id: selectedDevice,
            channel: detail.active_channel ?? segment.channel,
            action: 'stop',
          });
        } catch (stopErr) {
          // Swallow stop errors so the retry still fires — the device
          // may have already finished the old stream since our 409.
          console.warn('[RemoteDownloads] auto-stop before retry failed:', stopErr);
        }
        // Small breathing room so the device's state machine settles.
        await new Promise(r => setTimeout(r, 600));
        setActivePlayback(null);
        setActivePlaybackSegment(null);
        toast.dismiss(toastId);
        await handleStartPlayback(segment, true);
        return;
      }
      if (e.response?.status === 409) {
        const detail = e.response.data?.detail || {};
        toast.error(
          detail.message || 'Device is already playing back another segment. Stop current playback before starting a new one.',
          { duration: 7000 },
        );
        return;
      }
      toast.error(e.response?.data?.detail || e.message || 'Failed to start remote playback');
    }
  };

  const handleStopPlayback = async () => {
    if (!activePlayback) return;
    try {
      await api.post('/media/remote-media/playback/stop', {
        device_id: activePlayback.device_id,
        channel: activePlayback.channel,
        action: 'stop',
      });
      setActivePlayback(null);
      setActivePlaybackSegment(null);
      toast.success('Remote playback stopped');
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to stop playback');
    }
  };

  const handleSeekPlayback = async (targetIso, seekMeta = {}) => {
    if (!activePlayback || !activePlaybackSegment) return;
    const toastId = toast.loading('Seeking remote playback…');
    const seekStartBcd = activePlaybackSegment.start_bcd
      ? addSecondsToBcd(activePlaybackSegment.start_bcd, seekMeta.offsetSeconds || 0)
      : null;
    try {
      const res = await api.post('/media/remote-media/playback/seek', {
        device_id: activePlayback.device_id,
        channel: activePlayback.channel,
        start_time: targetIso,
        end_time: activePlaybackSegment.end_time || activePlayback.range_end_utc || activePlayback.end_time,
        start_bcd: seekStartBcd || undefined,
        end_bcd: activePlaybackSegment.end_bcd || activePlayback.end_bcd || undefined,
        stream_type: activePlayback.stream_type || activePlaybackSegment.stream_type || streamType,
        resource_type: activePlaybackSegment.resource_type || 0,
        memory_type: activePlayback.storage_type ?? activePlayback.memory_type ?? activePlaybackSegment.memory_type_value ?? parseInt(storageType, 10),
        source_file_id: activePlayback.source_file_id || activePlaybackSegment.id,
        media_timezone: activePlayback.media_timezone || mediaTz,
        previous_session_id: activePlayback.id,
      });
      setActivePlayback(res.data.session);
      toast.success('Playback jumped to selected point', { id: toastId });
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || e.message || 'Failed to seek playback', { id: toastId });
      throw e;
    }
  };

  const handleDownloadSegment = async (segment, mode = 'exact') => {
    if (!selectedDevice) return;
    const id = segment?.id || 'custom-window';
    setDownloadLoadingId(id);
    try {
      const res = await api.post('/media/remote-media/download', {
        device_id: selectedDevice,
        channel: segment?.channel || [...selectedChannels][0] || 1,
        start_time: segment?.start_time || localDayTimeToDeviceIso(selectedDate, startTime, [0, 0, 0]),
        end_time: segment?.end_time || localDayTimeToDeviceIso(selectedDate, endTime, [23, 59, 59]),
        storage_type: segment?.memory_type_value ?? parseInt(storageType, 10),
        stream_type: segment?.stream_type || streamType,
        video_type: segment?.video_type || videoType,
        mode,
        // Pass the synthetic segment.id (string) for FE traceability AND the
        // numeric event_code (16-bit, from the device media index) which lets
        // the backend request the most precise clip variant available.
        source_file_id: segment?.id,
        event_code: segment?.event_code ?? null,
        start_bcd: segment?.start_bcd,
        end_bcd: segment?.end_bcd,
        display_timezone: selectedTz,
        media_timezone: mediaTz,
      });
      const modeLabel = mode === 'custom'
        ? 'Custom time-window download requested'
        : res?.data?.a9f_mode === 'A'
          ? 'Alarm-event clip download requested'
          : res?.data?.a9f_mode === 'B'
            ? 'Whole-clip download requested'
            : 'Video download requested';
      toast.success(modeLabel);
      fetchDownloads();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to request download');
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const handleDeleteDownload = async (downloadId) => {
    if (!downloadId) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/media/remote/download/${downloadId}`);
      toast.success('Download deleted');
      setDeleteTarget(null);
      fetchDownloads();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to delete download');
    } finally {
      setDeleteLoading(false);
    }
  };

  /**
   * Submit a Custom Date download from the new dialog.
   * Called by ``CustomDownloadDialog`` below.  Wraps ``handleDownloadSegment``
   * by pre-building a synthetic "null segment" with an explicit channel, start
   * and end time and dispatching mode='custom' which routes to CE1 on the
   * backend (A9F/CE1 selection is done server-side based on mode+event_code).
   */
  const handleSubmitCustomDownload = async ({ channel, startIso, endIso, outputFormat = 'mp4' }) => {
    if (!selectedDevice) return;
    setDownloadLoadingId('custom-window');
    try {
      const res = await api.post('/media/remote-media/download', {
        device_id: selectedDevice,
        channel,
        start_time: startIso,
        end_time: endIso,
        storage_type: parseInt(storageType, 10),
        stream_type: streamType,
        video_type: videoType,
        mode: 'custom',
        output_format: outputFormat,
        display_timezone: selectedTz,
        media_timezone: mediaTz,
      });
      toast.success('Custom time-window download requested');
      setCustomDialogOpen(false);
      fetchDownloads();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Failed to request custom download');
    } finally {
      setDownloadLoadingId(null);
    }
  };

  const toggleChannel = (channel) => {
    setSelectedChannels(prev => {
      const next = new Set(prev);
      if (next.has(channel)) next.delete(channel);
      else next.add(channel);
      return next;
    });
  };

  const visibleSegments = useMemo(
    () => segments.filter(s => selectedChannels.has(s.channel)),
    [segments, selectedChannels],
  );

  // Reset segment-list pagination back to page 1 whenever the visible set
  // changes (new search, channel toggle, or new page size).
  useEffect(() => {
    setSegmentListPage(1);
  }, [visibleSegments.length, segmentListPageSize]);

  const handleTimelineMouseMove = (event, channelId) => {
    if (!selectedDate) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
    // iter148 — map the mouse position to the CURRENT zoom window rather
    // than always to the full 24h day, otherwise the hover tooltip prints
    // the wrong wall-clock time while the user is zoomed in.
    const windowSpan = Math.max(1, zoomEnd - zoomStart);
    const seconds = Math.max(0, Math.min(86399, Math.round(zoomStart + (x / rect.width) * windowSpan)));
    const displayIso = wallIsoFromDateAndSeconds(selectedDate, seconds);
    setTimelineHover({
      channelId,
      x,
      y: event.clientY - rect.top,
      displayIso,
    });
  };

  const monthDays = useMemo(() => buildMonthDays(monthDate), [monthDate]);
  const selectedKey = selectedDate ? dateKey(selectedDate) : null;

  // iter148 — Timeline zoom helpers.
  const timelineTicks = useMemo(() => buildTimelineTicks(zoomStart, zoomEnd), [zoomStart, zoomEnd]);
  const isZoomed = zoomStart !== 0 || zoomEnd !== 86400;
  const resetZoom = useCallback(() => { setZoomStart(0); setZoomEnd(86400); }, []);
  // iter148.2 — Brush track end labels are STATIC (`00:00:00` / `24:00:00`)
  // because the track itself always represents the full 24h domain. The
  // CURRENT zoom range — which changes as the user drags — is surfaced
  // separately in the Reset-zoom button so it stays visible without
  // misleading the eye about where on the brush track the selection sits.
  const formatZoomTime = (sec) => `${pad(Math.floor(sec / 3600))}:${pad(Math.floor((sec % 3600) / 60))}:${pad(sec % 60)}`;
  const zoomRangeLabel = isZoomed
    ? `${formatZoomTime(zoomStart)} → ${zoomEnd >= 86400 ? '24:00:00' : formatZoomTime(zoomEnd)}`
    : '';

  // Global pointermove/pointerup listener for the zoom slider. We attach
  // on window so the user can drag past the track edges without losing
  // the handle. The drag state captures the starting zoom window so that
  // the math is always relative to drag-start, not the live state.
  useEffect(() => {
    if (!zoomDrag) return undefined;
    const onMove = (ev) => {
      const d = zoomDragRef.current;
      if (!d) return;
      const dxPx = ev.clientX - d.startX;
      const deltaSec = (dxPx / d.rectWidth) * 86400;
      const MIN_WIDTH = 30; // minimum zoom window — 30 seconds
      if (d.type === 'left') {
        let next = Math.round(d.startZoomStart + deltaSec);
        next = Math.max(0, Math.min(d.startZoomEnd - MIN_WIDTH, next));
        setZoomStart(next);
      } else if (d.type === 'right') {
        let next = Math.round(d.startZoomEnd + deltaSec);
        next = Math.max(d.startZoomStart + MIN_WIDTH, Math.min(86400, next));
        setZoomEnd(next);
      } else if (d.type === 'window') {
        const span = d.startZoomEnd - d.startZoomStart;
        let next = Math.round(d.startZoomStart + deltaSec);
        next = Math.max(0, Math.min(86400 - span, next));
        setZoomStart(next);
        setZoomEnd(next + span);
      }
    };
    const onUp = () => { setZoomDrag(null); zoomDragRef.current = null; };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [zoomDrag]);

  const beginZoomDrag = (type) => (event) => {
    event.preventDefault();
    event.stopPropagation();
    const track = event.currentTarget.closest('[data-zoom-track="true"]');
    if (!track) return;
    const rect = track.getBoundingClientRect();
    zoomDragRef.current = {
      type,
      rectWidth: rect.width,
      startX: event.clientX,
      startZoomStart: zoomStart,
      startZoomEnd: zoomEnd,
    };
    setZoomDrag(type);
  };

  return (
    <div className="space-y-4" data-testid="remote-media-tab">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Clapperboard className="w-5 h-5 text-foreground" />
            <h2 className="text-xl font-semibold">Remote Media</h2>
          </div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">Search device recordings, play footage remotely, and download clips when needed</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { setDownloadsDateRange({ from: undefined, to: undefined }); setDownloadsDateResetKey(k => k + 1); fetchDevices(); fetchDownloads(); }} className="gap-1" data-testid="remote-media-refresh-downloads-button">
            <RefreshCw className="w-3 h-3" /> Refresh
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start" data-testid="remote-media-workspace">
        <aside className="space-y-3 rounded-lg border bg-card p-3 lg:w-80 lg:flex-none" data-testid="remote-media-control-panel">
          <div className="space-y-2">
            <Label className="flex items-center gap-2 text-sm"><HardDrive className="h-4 w-4" /> Device</Label>
            <Popover open={deviceDropdownOpen} onOpenChange={setDeviceDropdownOpen}>
              <div className="flex gap-2">
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 min-w-0 flex-1 justify-between px-3 text-left font-normal dark:bg-[#172031]"
                    data-testid="remote-media-device-select"
                  >
                    {selectedDeviceObj ? (
                      <span className="flex min-w-0 items-center gap-2">
                        <CameraRecordingStatusDot device={selectedDeviceObj} testId={`remote-media-selected-device-status-${selectedDeviceObj.id}`} />
                        <span className="min-w-0 truncate">{selectedDeviceObj.name}</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">Select device</span>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                {selectedDevice && (
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={clearRemoteMediaDeviceSelection}
                    title="Clear selected device"
                    aria-label="Clear selected device"
                    data-testid="remote-media-clear-selected-device-button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <PopoverContent className="z-[10000] w-[var(--radix-popover-trigger-width)] p-0" align="start">
                {selectedDevice && (
                  <button
                    type="button"
                    onClick={clearRemoteMediaDeviceSelection}
                    className="flex w-full items-center gap-2 border-b px-3 py-2 text-left text-xs font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    data-testid="remote-media-device-clear-option"
                  >
                    <X className="h-3.5 w-3.5" />
                    Clear selected device
                  </button>
                )}
                <div className="border-b bg-popover p-2">
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      ref={deviceSearchInputRef}
                      value={deviceSearchQuery}
                      onChange={(e) => setDeviceSearchQuery(e.target.value)}
                      placeholder="Search by name or IMEI..."
                      className="h-8 pl-7 pr-7 text-xs"
                      data-testid="remote-media-device-search"
                    />
                    {deviceSearchQuery && (
                      <button
                        type="button"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDeviceSearchQuery(''); deviceSearchInputRef.current?.focus(); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        data-testid="remote-media-device-search-clear"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground" data-testid="remote-media-device-search-count">
                    {filteredDevices.length}/{devices.length} devices
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto p-1" data-testid="remote-media-device-search-results">
                  {filteredDevices.length === 0 ? (
                    <div className="px-3 py-3 text-center text-xs text-muted-foreground" data-testid="remote-media-device-search-empty">
                      No devices match your search
                    </div>
                  ) : filteredDevices.map(d => (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => selectRemoteMediaDevice(d.id)}
                      className={`flex w-full min-w-0 items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground ${selectedDevice === d.id ? 'bg-accent text-accent-foreground' : ''}`}
                      data-testid={`remote-media-device-option-${d.id}`}
                    >
                      <CameraRecordingStatusDot device={d} testId={`remote-media-device-status-${d.id}`} />
                      <span className="min-w-0 truncate">{deviceDisplayName(d)}</span>
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <div className="rounded-md border bg-background p-2" data-testid="remote-media-calendar">
            <div className="mb-2 flex items-center justify-between">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))}
                disabled={!!selectedDevice && monthLoading}
                title={(!!selectedDevice && monthLoading) ? `Wait for ${monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} to load` : 'Previous month'}
                data-testid="remote-media-calendar-prev-month"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2 text-sm font-semibold">
                {/* iter146: ``undefined`` for the locale arg defers to
                    the browser/OS locale (which is the same path the
                    rest of the app's formatDT uses).  Was hardcoded
                    'en-AU' which ignored user preferences. */}
                <span>{monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}</span>
                {!!selectedDevice && monthLoading && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-medium text-sky-700 dark:bg-sky-950/60 dark:text-sky-300"
                    data-testid="remote-media-calendar-loading-pill"
                  >
                    <Loader2 className="h-2.5 w-2.5 animate-spin" /> Loading {monthDate.toLocaleDateString(undefined, { month: 'short' })}…
                  </span>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))}
                disabled={!!selectedDevice && monthLoading}
                title={(!!selectedDevice && monthLoading) ? `Wait for ${monthDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} to load` : 'Next month'}
                data-testid="remote-media-calendar-next-month"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-7 gap-1 text-center text-[11px] text-muted-foreground">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day}>{day}</div>)}
            </div>
            <div className="mt-1 grid grid-cols-7 gap-1">
              {monthDays.map(day => {
                const key = dateKey(day);
                const meta = calendarDays[key];
                const inMonth = day.getMonth() === monthDate.getMonth();
                const isSelected = selectedKey === key;
                const colorClass = meta?.has_alarm ? 'bg-red-500 text-white hover:bg-red-600' : meta?.has_normal ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'hover:bg-muted';
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => { if (!inMonth) return; handleCancelActiveSearch(); setSelectedDate(day); }}
                    disabled={!inMonth}
                    className={`relative h-8 rounded-full text-xs transition-colors ${inMonth ? colorClass : 'text-muted-foreground/40'} ${isSelected ? 'ring-2 ring-slate-900 ring-offset-2 dark:ring-white' : ''}`}
                    data-testid={`remote-media-calendar-day-${key}`}
                  >
                    {day.getDate()}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex items-center gap-3 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />Normal</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />Alarm</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full ring-2 ring-slate-900 dark:ring-white" />Selected</span>
            </div>
          </div>

          <Button onClick={() => handleQueryCalendar(false, selectedDevice, monthDate, true)} disabled={calendarLoading || !selectedDevice} variant="outline" className="w-full gap-2" data-testid="remote-media-refresh-calendar-button">
            {calendarLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarIcon className="h-4 w-4" />} Refresh Calendar
          </Button>

          <div className="space-y-2" data-testid="remote-media-primary-actions">
            <Button onClick={handleSearchSegments} disabled={searchLoading || !selectedDevice} className="w-full gap-2 bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="remote-media-search-button">
              {searchLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search Videos
            </Button>
            {searchLoading && (
              <Button onClick={handleCancelActiveSearch} variant="outline" size="sm" className="w-full gap-2" data-testid="remote-media-cancel-search-button">
                <X className="h-3.5 w-3.5" /> Cancel search
              </Button>
            )}
            <Button onClick={() => setCustomDialogOpen(true)} disabled={!selectedDevice || downloadLoadingId === 'custom-window'} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="remote-media-download-custom-window-button">
              {downloadLoadingId === 'custom-window' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download Custom Date
            </Button>
          </div>

          {calendarCached?.at && (
            <div className="text-[11px] text-muted-foreground" data-testid="remote-media-calendar-cache-label">
              {/* iter146: ``calendarCached.at`` is the server-side
                  UTC timestamp of when the AA4 calendar was cached.
                  Use formatDT (not formatWallClock) so the user sees
                  it in THEIR display timezone instead of UTC. */}
              Last refreshed: {formatDT(calendarCached.at, { device: selectedDeviceObj, seconds: false })} (cached)
            </div>
          )}

          <div className="rounded-md border bg-slate-50 px-3 py-2 text-xs text-muted-foreground dark:bg-slate-900/40" data-testid="remote-media-device-timezone">
            Display timezone: <span className="font-medium text-foreground">{selectedTz || 'Not resolved'}</span>
            <div>Device media timezone: <span className="font-medium text-foreground">{mediaTz}</span></div>
          </div>

          <div className="grid grid-cols-1 gap-2">
            <TimeStepper label="Start" value={startTime} onChange={setStartTime} testId="remote-media-start-time" />
            <TimeStepper label="End" value={endTime} onChange={setEndTime} testId="remote-media-end-time" />
          </div>

          <div className="space-y-2">
            <Select value={storageType} onValueChange={handleStorageTypeChange}>
              <SelectTrigger className="dark:bg-[#172031]" data-testid="remote-media-storage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="0" data-testid="remote-media-storage-option-all">All Storage</SelectItem>
                <SelectItem value="2" disabled={selectedDeviceIsMeitrack} data-testid="remote-media-storage-option-sd1">SD Card 1</SelectItem>
                <SelectItem value="3" disabled={selectedDeviceIsMeitrack} data-testid="remote-media-storage-option-sd2">SD Card 2</SelectItem>
              </SelectContent>
            </Select>
            <Select value={streamType} onValueChange={setStreamType}>
              <SelectTrigger className="dark:bg-[#172031]" data-testid="remote-media-stream-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Streams</SelectItem>
                <SelectItem value="main">Main Stream</SelectItem>
                <SelectItem value="sub">Sub Stream</SelectItem>
              </SelectContent>
            </Select>
            <Select value={videoType} onValueChange={setVideoType}>
              <SelectTrigger className="dark:bg-[#172031]" data-testid="remote-media-video-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Videos</SelectItem>
                <SelectItem value="normal">Normal Videos</SelectItem>
                <SelectItem value="alarm">Alarm Videos</SelectItem>
              </SelectContent>
            </Select>
          </div>

        </aside>

        <section className="min-w-0 flex-1 space-y-4">
          {/* Remote Playback now opens in ``RemotePlaybackModal`` using
              native WebCodecs transport, so the timeline and Recent
              Downloads can use the full column when nothing is playing. */}

          <Card className="overflow-hidden" data-testid="remote-media-timeline-card">
            <div className="flex items-center justify-between border-b bg-slate-50 px-3 py-2 dark:bg-slate-900/40">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <Activity className="h-3.5 w-3.5" /> Timeline
              </div>
              <div className="flex items-center gap-2">
                {isZoomed && (
                  <button
                    type="button"
                    onClick={resetZoom}
                    className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1 text-xs text-sky-700 hover:bg-sky-50 dark:text-sky-300 dark:hover:bg-sky-950/40"
                    title="Reset zoom to full day"
                    data-testid="remote-media-timeline-zoom-reset"
                  >
                    <X className="h-3 w-3" />
                    <span>Reset zoom</span>
                    <span className="text-muted-foreground tabular-nums" data-testid="remote-media-timeline-zoom-range-label">· {zoomRangeLabel}</span>
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setListExpanded(v => !v)}
                  className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1 text-xs text-muted-foreground hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-expanded={listExpanded}
                  data-testid="remote-media-toggle-list-button"
                  title={listExpanded ? 'Hide list' : 'Show list'}
                >
                  <List className="h-3 w-3" />
                  {/* iter148.3 — while a search is collecting we don't yet
                      have a final `segments` array (Task C: batch render
                      on complete), so surface the running parsed count
                      here instead of "0 segments" which would be
                      misleading. */}
                  {searchLoading ? (
                    <span data-testid="remote-media-segment-count" className="inline-flex items-center gap-1">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Searching… {searchParsedCount} found
                    </span>
                  ) : (
                    <span data-testid="remote-media-segment-count">{visibleSegments.length} segment{visibleSegments.length === 1 ? '' : 's'}</span>
                  )}
                  {listExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                </button>
              </div>
            </div>
            {/* iter146 redesign: graph is ALWAYS visible (so the channel
                checkboxes are always reachable).  The list is a
                collapsible panel that pushes open BELOW the graph. */}
            <div className="overflow-x-auto" data-testid="remote-media-timeline-view">
                <div className="min-w-[920px]">
                  {/* iter148.2 — channel column reduced 150px → 110px so the
                      gap between row labels (Cabin / Front / Zoom) and the
                      start of the timeline / brush is tight rather than
                      cavernous. Both rows now share the SAME grid template,
                      so timeline `00:00` and brush left-edge sit at the
                      identical x-coordinate (both `left:0%` of cell-1fr). */}
                  <div className="grid grid-cols-[110px_1fr] border-b text-xs text-muted-foreground">
                    <div className="p-2">Channel</div>
                    {/* `mr-6` adds breathing room on the right so the
                        trailing `24:00` tick can render without clipping. */}
                    <div className="relative h-8 p-2 mr-6" data-testid="remote-media-timeline-ticks">
                      {timelineTicks.map((tick, idx) => {
                        const pct = Math.max(0, Math.min(100, tick.pct));
                        // iter148.2 — first tick anchors LEFT, last tick
                        // anchors RIGHT so the labels never visually leak
                        // past the cell edges (which would overlap the
                        // channel column on the left or get clipped by the
                        // overflow-x scroll on the right).
                        const transform = pct < 5 ? '' : pct > 95 ? '-translate-x-full' : '-translate-x-1/2';
                        return (
                          <span
                            key={`${tick.seconds}-${idx}`}
                            className={`absolute top-2 text-[11px] tabular-nums ${transform}`}
                            style={{ left: `${pct}%` }}
                          >
                            {tick.label}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  {channelList.map(ch => (
                    <div key={ch.id} className="grid min-h-[46px] grid-cols-[110px_1fr] border-b last:border-b-0" data-testid={`remote-media-channel-row-${ch.id}`}>
                      <label className="flex items-center gap-2 px-2 text-sm">
                        <input type="checkbox" checked={selectedChannels.has(ch.id)} onChange={() => toggleChannel(ch.id)} data-testid={`remote-media-channel-checkbox-${ch.id}`} />
                        <Camera className="h-3.5 w-3.5" /> {ch.label}
                      </label>
                      <div
                        className="relative bg-[linear-gradient(to_right,rgba(148,163,184,.25)_1px,transparent_1px)] bg-[length:calc(100%/24)_100%] mr-6"
                        onMouseMove={(event) => handleTimelineMouseMove(event, ch.id)}
                        onMouseLeave={() => setTimelineHover(null)}
                        data-testid={`remote-media-timeline-grid-${ch.id}`}
                      >
                        {segments.filter(s => s.channel === ch.id).map(segment => {
                          const start = localIsoToSeconds(segment.start_time);
                          const end = Math.max(start + 60, localIsoToSeconds(segment.end_time, { endOfDay: true }));
                          // iter148 — zoom-aware rendering. Drop segments
                          // fully outside the current zoom window and clip
                          // straddling segments to the visible window so
                          // their width reflects only the visible portion.
                          if (end <= zoomStart || start >= zoomEnd) return null;
                          const clipStart = Math.max(start, zoomStart);
                          const clipEnd = Math.min(end, zoomEnd);
                          const span = Math.max(1, zoomEnd - zoomStart);
                          const left = ((clipStart - zoomStart) / span) * 100;
                          const width = Math.max(0.45, ((clipEnd - clipStart) / span) * 100);
                          return (
                            <button
                              key={segment.id}
                              type="button"
                              className={`absolute top-2 h-7 rounded-sm ${segment.video_type === 'alarm' ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-500 hover:bg-emerald-600'} text-white shadow-sm transition-colors`}
                              style={{ left: `${left}%`, width: `${width}%` }}
                              onClick={() => setSegmentInfoTarget(segment)}
                              title={segment.video_type === 'alarm'
                                ? `Alarm — ${segment.event_name || `Event ${segment.event_code}`} (${segment.event_code})`
                                : 'Normal recording'}
                              data-testid={`remote-media-timeline-segment-${segment.id}`}
                            />
                          );
                        })}
                        {timelineHover?.channelId === ch.id && (
                          <div
                            className="pointer-events-none absolute z-20 -translate-x-1/2 rounded-md bg-slate-950 px-2.5 py-1.5 text-[11px] text-white shadow-lg"
                            style={{ left: timelineHover.x, top: -34 }}
                            data-testid="remote-media-timeline-hover-tooltip"
                          >
                            <div>{formatWallClock(timelineHover.displayIso)}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* iter148.2 — Brush row: the track now spans the full 1fr
                      cell (no inline labels eating the start/end), so its
                      left/right edges line up EXACTLY with the timeline
                      ticks above. Static `00:00:00` / `24:00:00` labels
                      sit below the track at its anchored ends; the dynamic
                      zoom range is shown in the "Reset zoom" pill instead.
                      Handles are intentionally narrow (4px) like the
                      Forensic Graph Brush. */}
                  <div className="grid grid-cols-[110px_1fr] border-t bg-slate-50/60 dark:bg-slate-900/30" data-testid="remote-media-timeline-zoom-row">
                    <div className="flex items-center px-2 py-2 text-[11px] text-muted-foreground">
                      <span>Zoom</span>
                    </div>
                    <div className="py-2 mr-6">
                      <div
                        data-zoom-track="true"
                        className="relative h-3 rounded-sm bg-slate-200 dark:bg-slate-800 select-none"
                        data-testid="remote-media-timeline-zoom-range"
                      >
                        {/* Faint hour ticks for context */}
                        {Array.from({ length: 25 }, (_, i) => (
                          <span
                            key={i}
                            className="pointer-events-none absolute top-0 h-full w-px bg-slate-300/80 dark:bg-slate-700"
                            style={{ left: `${(i / 24) * 100}%` }}
                          />
                        ))}
                        {/* Selected window (draggable) */}
                        <div
                          role="slider"
                          aria-label="Timeline zoom window"
                          aria-valuemin={0}
                          aria-valuemax={86400}
                          aria-valuenow={zoomStart}
                          className={`absolute top-0 h-full bg-sky-500/40 ring-1 ring-inset ring-sky-600/60 dark:bg-sky-400/30 dark:ring-sky-400/60 ${zoomDrag === 'window' ? 'cursor-grabbing' : 'cursor-grab'}`}
                          style={{ left: `${(zoomStart / 86400) * 100}%`, width: `${Math.max(0.3, ((zoomEnd - zoomStart) / 86400) * 100)}%` }}
                          onPointerDown={beginZoomDrag('window')}
                          data-testid="remote-media-timeline-zoom-window"
                        />
                        {/* Left handle — Brush-style narrow vertical bar */}
                        <button
                          type="button"
                          aria-label="Zoom start handle"
                          className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-5 w-1 rounded-sm bg-sky-600 hover:bg-sky-700 dark:bg-sky-400 dark:hover:bg-sky-300 shadow touch-none cursor-ew-resize ${zoomDrag === 'left' ? 'bg-sky-800 dark:bg-sky-200' : ''}`}
                          style={{ left: `${(zoomStart / 86400) * 100}%` }}
                          onPointerDown={beginZoomDrag('left')}
                          data-testid="remote-media-timeline-zoom-handle-left"
                        />
                        {/* Right handle — Brush-style narrow vertical bar */}
                        <button
                          type="button"
                          aria-label="Zoom end handle"
                          className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 h-5 w-1 rounded-sm bg-sky-600 hover:bg-sky-700 dark:bg-sky-400 dark:hover:bg-sky-300 shadow touch-none cursor-ew-resize ${zoomDrag === 'right' ? 'bg-sky-800 dark:bg-sky-200' : ''}`}
                          style={{ left: `${(zoomEnd / 86400) * 100}%` }}
                          onPointerDown={beginZoomDrag('right')}
                          data-testid="remote-media-timeline-zoom-handle-right"
                        />
                      </div>
                      {/* Static end-of-domain labels, anchored to the same
                          x-coordinates as the timeline `00:00` and `24:00`
                          ticks above. */}
                      <div className="relative mt-1 h-4 text-[11px] tabular-nums">
                        <span
                          className="absolute top-0 left-0 font-medium text-foreground"
                          data-testid="remote-media-timeline-zoom-label-start"
                        >
                          00:00:00
                        </span>
                        <span
                          className="absolute top-0 right-0 font-medium text-foreground"
                          data-testid="remote-media-timeline-zoom-label-end"
                        >
                          24:00:00
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            {listExpanded && (
              <div className="border-t" data-testid="remote-media-list-view">
              <Table>
                <TableHeader><TableRow><TableHead>Channel</TableHead><TableHead>Time</TableHead><TableHead>Type</TableHead><TableHead>Stream</TableHead><TableHead>Size</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                <TableBody>
                  {visibleSegments.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="py-8 text-center text-muted-foreground">No device videos found for this search.</TableCell></TableRow>
                  ) : paginateArray(visibleSegments, segmentListPage, segmentListPageSize).map(segment => (
                    <TableRow key={segment.id} data-testid={`remote-media-list-row-${segment.id}`}>
                      <TableCell><Badge variant="outline">{channelLabelMap[segment.channel] || `CH${segment.channel}`}</Badge></TableCell>
                      <TableCell className="text-xs">
                        <div className="font-medium" data-testid={`remote-media-list-display-time-${segment.id}`}>{formatWallClock(segment.start_time)} → {formatWallClock(segment.end_time, { includeDate: false })}</div>
                        <div className="text-muted-foreground">Duration {durationLabel(segment.start_time, segment.end_time)}</div>
                        <div className="text-[10px] text-muted-foreground/80">Device media: {formatWallClock(segment.media_start_time)} UTC → {formatWallClock(segment.media_end_time, { includeDate: false })} UTC</div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={segment.video_type === 'alarm' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}
                          data-testid={`remote-media-list-type-${segment.id}`}
                        >
                          {segment.video_type === 'alarm'
                            ? (segment.event_name || `Event ${segment.event_code}`)
                            : 'Normal'}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize">{segment.stream_type}</TableCell>
                      <TableCell>{segment.file_size_display || '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => handleStartPlayback(segment)} data-testid={`remote-media-play-segment-${segment.id}`}><Play className="mr-1 h-3 w-3" />Play</Button>
                          <Button size="sm" variant="outline" onClick={() => handleDownloadSegment(segment, 'exact')} disabled={downloadLoadingId === segment.id} data-testid={`remote-media-download-segment-${segment.id}`}>{downloadLoadingId === segment.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <PaginationFooter
                page={segmentListPage}
                setPage={setSegmentListPage}
                totalItems={visibleSegments.length}
                itemsPerPage={segmentListPageSize}
                setItemsPerPage={setSegmentListPageSize}
                testIdPrefix="remote-media-list"
                alwaysShow
              />
              </div>
            )}
          </Card>

          <Card className="overflow-hidden" data-testid="remote-media-download-history-card">
            <CardContent className="flex min-h-0 flex-col p-0">
              <div className="flex flex-wrap items-center justify-between border-b px-3 py-2 gap-2">
                <div className="flex items-center gap-2 text-sm font-semibold whitespace-nowrap">
                  <Download className="h-4 w-4" /> Recent Downloads
                  <Badge variant="outline" className="ml-1" data-testid="remote-media-download-total">{totalDownloads}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2 flex-1 justify-end">
                  <div className="relative w-full max-w-[260px]">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      value={downloadsSearch}
                      onChange={(e) => setDownloadsSearch(e.target.value)}
                      placeholder="Search filename, IMEI, device…"
                      className="h-8 pl-8 pr-8 text-xs"
                      data-testid="remote-media-download-search"
                    />
                    {downloadsSearch && (
                      <button
                        type="button"
                        onClick={() => setDownloadsSearch('')}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                        data-testid="remote-media-download-search-clear"
                        title="Clear search"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  {/* Date range — Event Log "All Time" preset filter (shared component). */}
                  <DateRangePresetFilter value={downloadsDateRange} onChange={setDownloadsDateRange} testIdPrefix="remote-media-download-date" resetSignal={downloadsDateResetKey} triggerClassName="dark:bg-[#172031]" />
                </div>
              </div>
              {loadingDownloads ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#2563eb]" /></div>
              ) : downloads.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">No downloaded clips yet.</div>
              ) : (
                <>
                <div
                  className="scrollbar-corner-neutral min-h-0 overflow-x-auto"
                  data-testid="remote-media-download-history-scroll-area"
                >
                <div className="inline-block min-w-full align-top">
                <table className="w-max min-w-full table-auto caption-bottom text-sm">
                  <TableHeader className="sticky top-0 z-10 bg-card"><TableRow><TableHead className="whitespace-nowrap">Device</TableHead><TableHead className="whitespace-nowrap">Channel</TableHead><TableHead className="whitespace-nowrap">Time Frame</TableHead><TableHead className="whitespace-nowrap">File Size</TableHead><TableHead className="whitespace-nowrap">Status</TableHead><TableHead className="whitespace-nowrap">Progress</TableHead><TableHead className="whitespace-nowrap">Requested</TableHead><TableHead className="whitespace-nowrap text-center">Download</TableHead><TableHead className="whitespace-nowrap text-right">Actions</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {downloads.map(dl => {
                      const displayStatus = dl.storage_status === 'finalizing'
                        ? 'finalizing'
                        : (['missing', 'verifying'].includes(dl.storage_status) ? dl.storage_status : dl.status);
                      const StatusIcon = STATUS_ICON[displayStatus] || AlertCircle;
                      const canPlayDownload = dl.status === 'completed' && dl.storage_ready !== false;
                      const isAvmsg = dl.filename?.toLowerCase().endsWith('.avmsg');
                      const conversion = conversionStatusById[dl.id];
                      const serverPlaybackStatus = dl.playback_status;
                      const serverHasLivePlaybackStatus = ['queued', 'converting', 'ready', 'failed'].includes(serverPlaybackStatus);
                      const playbackStatus = serverHasLivePlaybackStatus ? serverPlaybackStatus : (conversion?.status || serverPlaybackStatus);
                      const playbackMessage = serverHasLivePlaybackStatus ? dl.playback_message : (conversion?.message || dl.playback_message);
                      const queuePosition = serverHasLivePlaybackStatus ? dl.playback_queue_position : (conversion?.playback_queue_position ?? dl.playback_queue_position);
                      const isConverting = playbackStatus === 'converting';
                      const isQueued = playbackStatus === 'queued';
                      const isAvmsgReady = isAvmsg && playbackStatus === 'ready' && dl.playback_ready !== false;
                      const needsAvmsgConversion = isAvmsg && canPlayDownload && !isAvmsgReady && !isConverting && !isQueued;
                      const canPlayVideo = canPlayDownload && (!isAvmsg || isAvmsgReady);
                      const rowDevice = deviceMap[dl.device_id];
                      // Phase 2C: prefer LIVE device label (gives `<Asset> / <Unit>`)
                      // — falls back to the backend's snapshot if the device
                      // was deleted or isn't in the current device list.
                      const rowDeviceLabel = (rowDevice ? deviceDisplayName(rowDevice) : null) || dl.device_name || 'Unknown Device';
                      // Time-frame column: device-timezone-aware start → end
                      const tzOpts = { device: rowDevice, seconds: true };
                      const startLabel = dl.start_time ? formatDT(dl.start_time, tzOpts) : null;
                      const endLabel = dl.end_time ? formatDT(dl.end_time, { ...tzOpts, includeDate: false }) : null;
                      return (
                        <TableRow key={dl.id} data-testid={`remote-media-download-row-${dl.id}`}>
                          <TableCell className="whitespace-nowrap"><div className="text-sm font-medium" title={rowDeviceLabel} data-testid={`remote-media-download-device-${dl.id}`}>{rowDeviceLabel}</div><div className="text-xs text-muted-foreground" title={dl.filename || dl.device_imei}>{dl.filename || dl.device_imei}</div></TableCell>
                          <TableCell className="whitespace-nowrap"><Badge variant="outline" data-testid={`remote-media-download-channel-${dl.id}`}>{rowDevice?.channel_names?.[String(dl.channel)] || `CH${dl.channel}`}</Badge></TableCell>
                          <TableCell className="whitespace-nowrap text-xs" data-testid={`remote-media-download-timeframe-${dl.id}`}>
                            {startLabel ? (
                              <div className="whitespace-nowrap">
                                <div className="font-medium">{startLabel}</div>
                                {endLabel && <div className="text-muted-foreground">→ {endLabel}</div>}
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs tabular-nums" data-testid={`remote-media-download-size-${dl.id}`}>{humanFileSize(dl.file_size)}</TableCell>
                          <TableCell className="whitespace-nowrap">
                            <Badge className={STATUS_BADGE[displayStatus] || ''} title={dl.storage_message || displayStatus} data-testid={`remote-media-download-status-${dl.id}`}>
                              <StatusIcon className={`mr-1 h-3 w-3 ${['pending', 'finalizing', 'verifying'].includes(displayStatus) ? 'animate-spin' : ''}`} />
                              {displayStatus}
                            </Badge>
                            {isAvmsg && (playbackStatus || needsAvmsgConversion) && (
                              <div className="mt-1">
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] ${isConverting || isQueued ? 'border-amber-300 bg-amber-50 text-amber-700' : isAvmsgReady ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : playbackStatus === 'failed' ? 'border-red-300 bg-red-50 text-red-700' : 'border-slate-300 bg-slate-50 text-slate-700'}`}
                                  title={playbackMessage || playbackStatus || 'Convert AVMSG before playback'}
                                  data-testid={`remote-media-download-conversion-status-${dl.id}`}
                                >
                                  {isConverting && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                                  {isAvmsgReady ? 'AVMSG ready' : playbackStatus === 'failed' ? 'AVMSG failed' : isConverting ? 'Converting AVMSG' : isQueued ? `Queued #${queuePosition || 1}` : 'Needs conversion'}
                                </Badge>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs">{dl.progress != null ? `${dl.progress}%` : '-'}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground" title={dl.created_at ? formatDT(dl.created_at, { device: rowDevice }) : '-'}>{dl.created_at ? formatDT(dl.created_at, { device: rowDevice }) : '-'}</TableCell>
                          <TableCell className="whitespace-nowrap text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownloadFile(dl)}
                              disabled={!canPlayDownload || downloadFileLoadingId === dl.id}
                              title={canPlayDownload ? 'Download original file' : (dl.storage_message || 'Stored file is not available yet')}
                              data-testid={`remote-media-download-file-${dl.id}`}
                            >
                              {downloadFileLoadingId === dl.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                            </Button>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right">
                            <div className="flex justify-end gap-1">
                              {needsAvmsgConversion && <Button size="sm" variant="outline" onClick={() => handleConvertDownload(dl)} title="Convert AVMSG for playback" data-testid={`remote-media-convert-download-${dl.id}`}><RefreshCw className="mr-1 h-3 w-3" />Convert</Button>}
                              {canPlayVideo && <Button size="sm" variant="outline" onClick={() => handlePlayDownload(dl)} title="Play video" data-testid={`remote-media-play-download-${dl.id}`}><Play className="mr-1 h-3 w-3" />Play</Button>}
                              {isAvmsg && isQueued && <Button size="sm" variant="outline" disabled title="Queued for conversion" data-testid={`remote-media-queued-download-${dl.id}`}><Clock className="mr-1 h-3 w-3" />Queued #{queuePosition || 1}</Button>}
                              {isAvmsg && isConverting && <Button size="sm" variant="outline" disabled title="Converting AVMSG" data-testid={`remote-media-converting-download-${dl.id}`}><Loader2 className="mr-1 h-3 w-3 animate-spin" />Converting</Button>}
                              {dl.status === 'completed' && !canPlayDownload && <Button size="sm" variant="outline" disabled title={dl.storage_message || 'Stored file is not available yet'} data-testid={`remote-media-play-unavailable-${dl.id}`}><AlertCircle className="mr-1 h-3 w-3" />No file</Button>}
                              {['pending', 'uploading'].includes(dl.status) && <Button size="icon" variant="ghost" onClick={() => handleCancelDownload(dl.id)} title="Cancel download" className="h-8 w-8 text-red-500" data-testid={`remote-media-cancel-download-${dl.id}`}><X className="h-3 w-3" /></Button>}
                              <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(dl)} title="Delete download" className="h-8 w-8 text-red-500" data-testid={`remote-media-delete-download-${dl.id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </table>
                </div>
                </div>
                </>
              )}
              <PaginationFooter
                page={downloadsPage}
                setPage={setDownloadsPage}
                totalItems={totalDownloads}
                itemsPerPage={downloadsPageSize}
                setItemsPerPage={setDownloadsPageSize}
                testIdPrefix="remote-media-download"
                zeroIndexed
                disabled={loadingDownloads}
                alwaysShow
              />
            </CardContent>
          </Card>
        </section>
      </div>

      <VideoPlayerModal
        open={playerOpen}
        onClose={handlePlayerOpenChange}
        video={playerVideo}
        videoUrl={playerUrl}
        device={playerVideo ? deviceMap[playerVideo.device_id] : null}
      />

      <RemotePlaybackModal
        open={Boolean(activePlayback)}
        onClose={() => { /* handled via onStop */ }}
        session={activePlayback}
        segment={activePlaybackSegment}
        channelLabel={activePlayback ? (channelLabelMap[activePlayback.channel] || `CH${activePlayback.channel}`) : null}
        deviceName={activePlayback ? (deviceDisplayName(deviceMap[activePlayback.device_id]) || activePlayback.device_name) : null}
        onStop={handleStopPlayback}
        onSeek={handleSeekPlayback}
        api={api}
      />

      <SegmentInfoModal
        open={Boolean(segmentInfoTarget)}
        onOpenChange={(next) => { if (!next) setSegmentInfoTarget(null); }}
        segment={segmentInfoTarget}
        channelLabel={segmentInfoTarget ? (channelLabelMap[segmentInfoTarget.channel] || `CH${segmentInfoTarget.channel}`) : null}
        onStream={(s) => handleStartPlayback(s)}
        onDownload={(s) => handleDownloadSegment(s, 'exact')}
        downloadLoading={Boolean(segmentInfoTarget && downloadLoadingId === segmentInfoTarget.id)}
      />

      <CustomDownloadDialog
        open={customDialogOpen}
        onOpenChange={setCustomDialogOpen}
        loading={downloadLoadingId === 'custom-window'}
        channelList={channelList}
        defaultChannel={[...selectedChannels][0] || (channelList[0] && channelList[0].id) || 1}
        initialDate={selectedDate}
        initialStart={startTime}
        displayTz={selectedTz}
        mediaTz={mediaTz}
        device={selectedDeviceObj}
        onSubmit={handleSubmitCustomDownload}
      />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="remote-media-delete-export-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Download?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the download from Recent Downloads and delete the stored video file if it exists.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteTarget && (
            <div className="rounded-md bg-muted px-3 py-2 text-sm" data-testid="remote-media-delete-export-summary">
              <div className="font-medium">{(deviceMap[deleteTarget.device_id] ? deviceDisplayName(deviceMap[deleteTarget.device_id]) : null) || deleteTarget.device_name || deleteTarget.device_imei}</div>
              <div className="text-muted-foreground">{deleteTarget.filename || `CH${deleteTarget.channel} download`}</div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteLoading} data-testid="remote-media-delete-export-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteLoading}
              onClick={(event) => {
                event.preventDefault();
                handleDeleteDownload(deleteTarget?.id);
              }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="remote-media-delete-export-confirm-button"
            >
              {deleteLoading ? 'Deleting...' : 'Delete download'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
